import { CommonModule } from '@angular/common';
import { HttpParams } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MenuItem, MessageService } from 'primeng/api';
import { BadgeModule } from 'primeng/badge';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { DialogModule } from 'primeng/dialog';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputTextModule } from 'primeng/inputtext';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputNumberModule } from 'primeng/inputnumber';
import { ProgressBarModule } from 'primeng/progressbar';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TabsModule } from 'primeng/tabs';
import { TagModule } from 'primeng/tag';
import { TextareaModule } from 'primeng/textarea';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { catchError, forkJoin, Observable, of } from 'rxjs';
import { Company } from '../../company/company.model';
import { CompanyService } from '../../company/company.service';
import { CrmService } from '../../crm/crm.service';
import { CrmAccount, CrmClient } from '../../crm/crm.model';
import { CustomerService } from '../../customer/customer.service';
import { Driver } from '../../fleet/driver/driver.model';
import { DriverService } from '../../fleet/driver/driver.service';
import { Location } from '../../location/location.model';
import { LocationService } from '../../location/location.service';
import { OrdersService } from '../../order/order.service';
import { PaymentTerm } from '../../order/models/payment-term.model';
import { ContractsService } from '../../contracts/contracts.service';
import { Contracts } from '../../contracts/models/contracts.model';
import { DeliveryRateCard } from '../../contracts/models/delivery-rate-card.model';
import { ShipmentIntakePricing } from '../../shipment/models/intake/shipment-intake.model';
import { LazyLocationPickerDirective } from '../../shared/location-picker/lazy-location-picker.directive';
import { VehicleMin } from '../../fleet/vehicle/vehicle-min.model';
import { VehicleService } from '../../fleet/vehicle/vehicle.service';
import { PermissionService } from '../../security/permission.service';
import { app_permissions } from '../../security/app-permissions';
import { SessionStorageService } from '../../services/session-storage.service';
import { PageDataPanelComponent } from '../../shared/page-layout/page-data-panel.component';
import { PageDetailHeroComponent } from '../../shared/page-layout/page-detail-hero.component';
import { PageMetric } from '../../shared/page-layout/page-layout.models';
import { PageMetricStripComponent } from '../../shared/page-layout/page-metric-strip.component';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import { AuditActorSummary } from '../../shared/audit/audit.models';
import { ContainerCaseEta } from '../models/container-case-eta.model';
import { ContainerCaseReadiness } from '../models/container-case-readiness.model';
import { ContainerCaseResourceLink } from '../models/container-case-resource-link.model';
import { ContainerCase } from '../models/container-case.model';
import { ContainerEvent } from '../models/container-event.model';
import { ContainerException } from '../models/container-exception.model';
import { ContainerStorageLeg } from '../models/container-storage-leg.model';
import { ContainerTransportLeg } from '../models/container-transport-leg.model';
import { ContainerJourneyDispatchCandidate, ContainerTrackingService } from '../container-tracking.service';
import { ContainerFacility } from '../models/container-facility.model';
import { ContainerLegHandoff, ContainerLegHandoffContext } from '../models/container-leg-handoff.model';
import {
  ContainerYardAppointment,
  ContainerYardArea,
  ContainerYardChargeAccrual,
  ContainerYardEir,
  ContainerYardMove,
  ContainerYardSlot,
  ContainerYardVisit
} from '../models/container-terminal-yard.model';
import { ContainerFacilityService } from '../container-facility.service';
import { ContainerTerminalYardService } from '../container-terminal-yard.service';
import { WarehousingMasterData } from '../../warehousing/warehousing.model';
import { WarehousingService } from '../../warehousing/warehousing.service';
import { VehicleType } from '../../fleet/vehicle/params/vehicle-type';
import { TransportPlanningService } from '../../transport-planning/transport-planning.service';
import { FieldResourcesPanelComponent } from '../../field-work/field-resources-panel.component';
import { PosmRouteTemplate, PosmTruckAssignmentRule } from '../../transport-planning/transport-planning.model';
import { LogisticsProject } from '../../logistics-projects/models/logistics-project.model';
import { LogisticsProjectsService } from '../../logistics-projects/logistics-projects.service';
import {
  ContainerJourneyPlanItem,
  ContainerJourneyPlanItemUpsertRequest,
  ContainerJourneyPlanView,
  ContainerJourneyTemplateView
} from '../models/container-journey-plan.model';

interface TimelineGroup {
  date: Date;
  label: string;
  eventCountLabel: string;
  events: ContainerEvent[];
}

@Component({
  selector: 'app-container-case-detail',
  standalone: true,
  imports: [
    CommonModule,
    BadgeModule,
    BreadcrumbModule,
    ButtonModule,
    DatePickerModule,
    DialogModule,
    IftaLabelModule,
    InputTextModule,
    InputGroupModule,
    InputGroupAddonModule,
    InputNumberModule,
    ProgressBarModule,
    FormsModule,
    ReactiveFormsModule,
    SelectModule,
    LazyLocationPickerDirective,
    TableModule,
    TabsModule,
    TagModule,
    TextareaModule,
    ToastModule,
    TooltipModule,
    PageDataPanelComponent,
    PageDetailHeroComponent,
    PageMetricStripComponent,
    PageShellComponent,
    FieldResourcesPanelComponent
  ],
  providers: [MessageService],
  templateUrl: './container-case-detail.component.html',
  styleUrls: ['./container-case-detail.component.css']
})
export class ContainerCaseDetailComponent implements OnInit {
  private readonly caseTabKeys = [
    { key: 'overview', value: 0 },
    { key: 'timeline', value: 1 },
    { key: 'transport-legs', value: 2 },
    { key: 'clearance', value: 3 },
    { key: 'storage-legs', value: 4 },
    { key: 'handoffs', value: 5 },
    { key: 'readiness', value: 6 },
    { key: 'journey-plan', value: 7 },
    { key: 'linked-records', value: 8 }
  ] as const;
  isLoading = false;
  isSubmitting = false;
  activeTabValue = 0;
  caseItem?: ContainerCase;
  links: ContainerCaseResourceLink[] = [];
  timeline: ContainerEvent[] = [];
  timelineGroups: TimelineGroup[] = [];
  clearanceEvents: ContainerEvent[] = [];
  exceptions: ContainerException[] = [];
  transportLegs: ContainerTransportLeg[] = [];
  storageLegs: ContainerStorageLeg[] = [];
  handoffs: ContainerLegHandoff[] = [];
  journeyPlans: ContainerJourneyPlanView[] = [];
  journeyTemplates: ContainerJourneyTemplateView[] = [];
  selectedJourneyTemplateId?: number;
  facilities: ContainerFacility[] = [];
  supplierClients: CrmClient[] = [];
  yardVisits: ContainerYardVisit[] = [];
  yardMovesByVisit = new Map<number, ContainerYardMove[]>();
  yardChargesByVisit = new Map<number, ContainerYardChargeAccrual[]>();
  yardEirsByVisit = new Map<number, ContainerYardEir[]>();
  yardAppointments: ContainerYardAppointment[] = [];
  yardAreas: ContainerYardArea[] = [];
  yardSlots: ContainerYardSlot[] = [];
  warehouseOptions: WarehousingMasterData[] = [];
  zoneOptions: WarehousingMasterData[] = [];
  etaLabel = '-';
  etaDetail = '';
  readiness?: ContainerCaseReadiness;
  projectDisplayLabel = '';
  customerCompanyDisplayLabel = '';
  customerContactDisplayLabel = '';
  crmClientDisplayLabel = '';
  crmAccountDisplayLabel = '';
  canEditCase = false;
  canManageLegs = false;
  canManageStorage = false;
  canCreateShipments = false;
  dispatchDialogVisible = false;
  dispatchPaymentTerms: PaymentTerm[] = [];
  dispatchPaymentTermId?: number;
  dispatchCanViewPricing = false;
  dispatchCurrency = 'SAR';
  dispatchRateSource = 'RATE_CARD';
  dispatchSellingPrice?: number | null;
  dispatchContract?: Contracts;
  dispatchRevisionId?: number;
  dispatchRateCard?: DeliveryRateCard;
  dispatchContracts: Contracts[] = [];
  dispatchContractsLoading = false;
  dispatchRateCards: DeliveryRateCard[] = [];
  dispatchOverrideReason = '';
  dispatchPricingWarnings: string[] = [];
  dispatchRateLoading = false;
  dispatchError = '';
  dispatchItem?: ContainerJourneyPlanItem;
  private dispatchPricingSequence = 0;
  readonly dispatchCurrencies = ['SAR', 'USD', 'AED'];
  readonly dispatchRateSources = [{ label: 'Rate Card', value: 'RATE_CARD' }, { label: 'Manual', value: 'MANUAL' }];
  dispatchCandidates: ContainerJourneyDispatchCandidate[] = [];
  selectedDispatchCandidates: ContainerJourneyDispatchCandidate[] = [];

  actorLabel(actor?: AuditActorSummary | null, actorId?: number | null): string {
    if (actor?.name) {
      return actor.id ? `${actor.name} (#${actor.id})` : actor.name;
    }
    if (actorId !== null && actorId !== undefined) {
      return `#${actorId}`;
    }
    return 'System';
  }

  drivers: Driver[] = [];
  vehicles: VehicleMin[] = [];
  vendors: Company[] = [];
  locations: Location[] = [];
  vehicleTypes: VehicleType[] = [];
  routeTemplates: PosmRouteTemplate[] = [];
  assignmentRules: PosmTruckAssignmentRule[] = [];
  selectedTransportLeg?: ContainerTransportLeg;
  selectedStorageLeg?: ContainerStorageLeg;
  selectedYardVisit?: ContainerYardVisit;

  storageDialogVisible = false;
  handoffDialogVisible = false;
  storageSlotDialogVisible = false;
  storageHoldDialogVisible = false;
  storageReleaseDialogVisible = false;
  yardGateInDialogVisible = false;
  yardGateOutDialogVisible = false;
  yardMoveDialogVisible = false;
  journeyItemDialogVisible = false;
  journeyEndMinimum?: Date;
  journeyItemDeleteDialogVisible = false;
  journeyTemplateDialogVisible = false;
  storageForm!: FormGroup;
  handoffForm!: FormGroup;
  storageSlotForm!: FormGroup;
  storageHoldForm!: FormGroup;
  storageReleaseForm!: FormGroup;
  yardGateInForm!: FormGroup;
  yardGateOutForm!: FormGroup;
  yardMoveForm!: FormGroup;
  journeyItemForm!: FormGroup;
  journeyTemplateForm!: FormGroup;
  editingJourneyItem?: ContainerJourneyPlanItem;
  journeyItemPendingDelete?: ContainerJourneyPlanItem;
  handoffAction: 'receiveStorage' | 'exitStorage' | null = null;

  transportModeOptions = [
    { label: 'Road Move', value: 'TRUCK' },
    { label: 'Yard Transfer', value: 'YARD_TRANSFER' },
    { label: 'Laden Delivery', value: 'CUSTOMER_DELIVERY' },
    { label: 'Terminal Handoff', value: 'TERMINAL_TRANSFER' },
    { label: 'Empty Return', value: 'EMPTY_RETURN' }
  ];

  releasePriorityOptions = [
    { label: 'Normal', value: 'NORMAL' },
    { label: 'High', value: 'HIGH' },
    { label: 'Urgent', value: 'URGENT' },
    { label: 'Low', value: 'LOW' }
  ];

  storageFacilityOptions = [
    { label: 'Port Terminal', value: 'PORT_TERMINAL' },
    { label: 'Bonded Yard', value: 'BONDED_YARD' },
    { label: 'CFS / Transit Shed', value: 'CFS' },
    { label: 'CFS / Warehouse Staging', value: 'WAREHOUSE' },
    { label: 'Customer Site', value: 'CUSTOMER_SITE' },
    { label: 'Empty Depot', value: 'EMPTY_DEPOT' },
    { label: 'Carrier Yard', value: 'CARRIER_YARD' },
    { label: '3PL Yard', value: 'THIRD_PARTY_YARD' }
  ];

  storageReasonOptions = [
    { label: 'Terminal Yard', value: 'TERMINAL_YARD' },
    { label: 'Third-party Terminal Reference', value: 'THIRD_PARTY_TERMINAL_REFERENCE' },
    { label: 'Port Dwell', value: 'PORT_DWELL' },
    { label: 'CFS Staging', value: 'CFS_STAGING' },
    { label: 'Bonded Storage', value: 'BONDED_STORAGE' },
    { label: 'Warehouse Staging', value: 'WAREHOUSE_STAGING' },
    { label: 'Customs Hold', value: 'CUSTOMS_HOLD' },
    { label: 'Inspection', value: 'INSPECTION' },
    { label: 'Empty Return Staging', value: 'EMPTY_RETURN_STAGING' }
  ];

  fullEmptyOptions = [
    { label: 'Full', value: 'FULL' },
    { label: 'Empty', value: 'EMPTY' },
    { label: 'Unknown', value: 'UNKNOWN' }
  ];

  conditionOptions = [
    { label: 'Good', value: 'GOOD' },
    { label: 'Damaged', value: 'DAMAGED' },
    { label: 'Unknown', value: 'UNKNOWN' }
  ];

  yesNoOptions = [
    { label: 'Yes', value: true },
    { label: 'No', value: false }
  ];

  breadcrumbItems: MenuItem[] = [
    { label: 'Container Tracking', routerLink: '/container-tracking/cases' },
    { label: 'Cases', routerLink: '/container-tracking/cases' },
    { label: 'Case Detail' }
  ];
  homeBreadcrumb: MenuItem = { icon: 'pi pi-home', routerLink: '/' };

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private fb: FormBuilder,
    private messageService: MessageService,
    private permissionService: PermissionService,
    private sessionStorageService: SessionStorageService,
    private driverService: DriverService,
    private vehicleService: VehicleService,
    private companyService: CompanyService,
    private locationService: LocationService,
    private containerTrackingService: ContainerTrackingService,
    private facilityService: ContainerFacilityService,
    private terminalYardService: ContainerTerminalYardService,
    private warehousingService: WarehousingService,
    private transportPlanningService: TransportPlanningService,
    private logisticsProjectsService: LogisticsProjectsService,
    private crmService: CrmService,
    private customerService: CustomerService,
    private ordersService: OrdersService,
    private contractsService: ContractsService
  ) {}

  ngOnInit(): void {
    this.canEditCase = this.permissionService.hasPermission(app_permissions.container_tracking.cases.edit);
    this.canManageLegs = this.permissionService.hasPermission(app_permissions.container_tracking.legs.manage);
    this.canCreateShipments = this.permissionService.hasPermission(app_permissions.shipments.CREATE);
    this.canManageStorage = this.permissionService.hasPermission(app_permissions.container_tracking.storage.manage);
    this.route.queryParamMap.subscribe(queryParams => {
      this.activeTabValue = this.resolveCaseTabValue(queryParams.get('tab'));
    });
    this.initForms();
    this.loadExecutionResources();
    this.loadCase();
  }

  get metrics(): PageMetric[] {
    return [
      { label: 'Status', value: this.caseItem?.status || '-', tone: this.statusTone(this.caseItem?.status) },
      { label: 'Movement Stage', value: this.movementStageLabel(this.caseItem?.currentState) },
      { label: 'Next Checkpoint', value: this.etaLabel, tone: 'info' },
      { label: 'Linked Records', value: this.links.length },
      { label: 'Handoffs', value: this.handoffs.length, tone: this.blockedHandoffCount() ? 'danger' : 'info' },
      { label: 'Readiness', value: this.readinessLabel(), tone: this.readinessTone() }
    ];
  }

  get riskBadge(): string {
    if (this.readiness?.inspectionBlocked || this.hasExceptionSeverity('danger')) {
      return 'BLOCKED';
    }
    if (this.readiness?.containerAvailable) {
      return 'READY';
    }
    if (this.hasExceptionSeverity('warn')) {
      return 'WATCH';
    }
    return 'NONE';
  }

  hasLogisticsProject(): boolean {
    return !!this.caseItem?.logisticsProjectId;
  }

  openProject(): void {
    if (!this.caseItem?.logisticsProjectId) {
      return;
    }
    this.router.navigate(['/logistics-projects', this.caseItem.logisticsProjectId]);
  }

  commercialWorkspaceReference(): { referenceType: 'BOOKING' | 'BILL_OF_LADING'; referenceNumber: string } | undefined {
    const bookingNumber = this.caseItem?.bookingNumber?.trim();
    if (bookingNumber) {
      return { referenceType: 'BOOKING', referenceNumber: bookingNumber };
    }
    const billOfLadingNumber = this.caseItem?.billOfLadingNumber?.trim();
    return billOfLadingNumber
      ? { referenceType: 'BILL_OF_LADING', referenceNumber: billOfLadingNumber }
      : undefined;
  }

  commercialWorkspaceLabel(): string {
    return this.commercialWorkspaceReference()?.referenceType === 'BOOKING'
      ? 'Open Booking Workspace'
      : 'Open BL Workspace';
  }

  openCommercialWorkspace(): void {
    const workspace = this.commercialWorkspaceReference();
    if (!workspace) {
      return;
    }
    this.router.navigate(['/container-tracking/bookings/view'], {
      queryParams: workspace
    });
  }

  projectContextLabel(): string {
    return this.projectDisplayLabel || this.firstSafeLinkLabel('LOGISTICS_PROJECT', 'PROJECT') || 'Project reference pending';
  }

  customerContextLabel(): string {
    const label = [this.customerCompanyDisplayLabel, this.customerContactDisplayLabel].filter(Boolean).join(' | ')
      || this.firstSafeLinkLabel('CUSTOMER_COMPANY', 'CUSTOMER');
    if (label) {
      return label;
    }
    if (this.caseItem?.customerCompanyId || this.caseItem?.customerContactId
      || this.caseItem?.crmClientId || this.caseItem?.crmAccountId) {
      return 'Customer reference pending';
    }
    return '-';
  }

  crmContextLabel(): string {
    const label = [this.crmClientDisplayLabel, this.crmAccountDisplayLabel].filter(Boolean).join(' | ')
      || this.firstSafeLinkLabel('CRM_CLIENT', 'CRM_ACCOUNT');
    if (label) {
      return label;
    }
    if (this.caseItem?.crmClientId || this.caseItem?.crmAccountId) {
      return 'CRM profile reference pending';
    }
    return 'CRM profile not linked';
  }

  loadCase(): void {
    const caseId = Number(this.route.snapshot.paramMap.get('id'));
    if (!caseId) {
      this.router.navigate(['/container-tracking/cases']);
      return;
    }
    this.isLoading = true;
    forkJoin({
      caseItem: this.containerTrackingService.findCaseById(caseId),
      links: this.containerTrackingService.findCaseLinks(caseId),
      timeline: this.containerTrackingService.findTimeline(caseId)
    }).subscribe({
      next: response => {
        this.caseItem = response.caseItem;
        this.links = response.links;
        this.timeline = response.timeline;
        this.timelineGroups = this.groupTimeline(response.timeline);
        this.clearanceEvents = response.timeline.filter(event => (event.sourceModule || '').toUpperCase() === 'CLEARANCE');
        this.breadcrumbItems = [
          { label: 'Container Tracking', routerLink: '/container-tracking/cases' },
          { label: 'Cases', routerLink: '/container-tracking/cases' },
          { label: response.caseItem.caseNumber || response.caseItem.containerNumber || 'Container Case' }
        ];
        this.loadReferenceContext();
        this.loadLivePanels();
      },
      error: () => {
        this.isLoading = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Could not load container case.' });
        this.router.navigate(['/container-tracking/cases']);
      }
    });
  }

  onTabChange(value: number | string): void {
    this.activeTabValue = Number(value);
    const selectedTab = this.caseTabKeys.find(tab => tab.value === this.activeTabValue);
    if (!selectedTab) {
      return;
    }
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { tab: selectedTab.key },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  loadLivePanels(): void {
    if (!this.caseItem) {
      return;
    }
    forkJoin({
      eta: this.containerTrackingService.getCaseEta(this.caseItem.id || 0),
      readiness: this.containerTrackingService.getCaseReadiness(this.caseItem.id || 0),
      exceptions: this.containerTrackingService.getCaseExceptions(this.caseItem.id || 0),
      transportLegs: this.containerTrackingService.findTransportLegs(this.caseItem.id || 0),
      storageLegs: this.containerTrackingService.findStorageLegs(this.caseItem.id || 0),
      handoffs: this.containerTrackingService.findHandoffs(this.caseItem.id || 0)
      ,
      journeyPlans: this.containerTrackingService.findJourneyPlans(this.caseItem.id || 0)
    }).subscribe({
      next: response => {
        this.applyEta(response.eta);
        this.readiness = response.readiness;
        this.exceptions = response.exceptions;
        this.transportLegs = response.transportLegs;
        this.storageLegs = response.storageLegs;
        this.handoffs = response.handoffs;
        this.journeyPlans = response.journeyPlans || [];
        this.isLoading = false;
        this.loadFacilityAndYardContext();
      },
      error: () => {
        this.isLoading = false;
        this.loadFacilityAndYardContext();
      }
    });
  }

  private loadReferenceContext(): void {
    const containerCase = this.caseItem;
    if (!containerCase) {
      return;
    }
    this.projectDisplayLabel = '';
    this.customerCompanyDisplayLabel = '';
    this.customerContactDisplayLabel = '';
    this.crmClientDisplayLabel = '';
    this.crmAccountDisplayLabel = '';

    const owningCompanyId = this.resolveOwningCompanyId();
    if (containerCase.logisticsProjectId && owningCompanyId) {
      this.logisticsProjectsService.getProject(containerCase.logisticsProjectId, owningCompanyId).subscribe({
        next: project => {
          this.projectDisplayLabel = this.logisticsProjectLabel(project);
          if (!this.customerCompanyDisplayLabel && project.customerCompanyId) {
            this.loadCustomerCompanyLabel(project.customerCompanyId);
          }
          if (!this.customerContactDisplayLabel && project.customerContactId) {
            this.loadCustomerContactLabel(project.customerContactId);
          }
          if (!this.crmClientDisplayLabel && project.crmClientId) {
            this.loadCrmClientLabel(project.crmClientId);
          }
          if (!this.crmAccountDisplayLabel && project.crmAccountId) {
            this.loadCrmAccountLabel(project.crmAccountId, project.crmClientId);
          }
        },
        error: () => {
          this.projectDisplayLabel = this.firstSafeLinkLabel('LOGISTICS_PROJECT', 'PROJECT') || 'Project reference unavailable';
        }
      });
    }

    if (containerCase.customerCompanyId) {
      this.loadCustomerCompanyLabel(containerCase.customerCompanyId);
    }
    if (containerCase.customerContactId) {
      this.loadCustomerContactLabel(containerCase.customerContactId);
    }
    if (containerCase.crmClientId) {
      this.loadCrmClientLabel(containerCase.crmClientId);
    }
    if (containerCase.crmAccountId) {
      this.loadCrmAccountLabel(containerCase.crmAccountId, containerCase.crmClientId);
    }
  }

  private loadCustomerCompanyLabel(companyId: number): void {
    this.companyService.getCompanyById(companyId).subscribe({
      next: response => {
        this.customerCompanyDisplayLabel = this.companyReferenceLabel(response.data);
      },
      error: () => {
        this.customerCompanyDisplayLabel = this.firstSafeLinkLabel('CUSTOMER_COMPANY', 'CUSTOMER') || 'Customer reference unavailable';
      }
    });
  }

  private loadCustomerContactLabel(customerContactId: number): void {
    this.customerService.getCustomerById(customerContactId).subscribe({
      next: response => {
        const contact = response.data;
        this.customerContactDisplayLabel = contact
          ? [contact.name, contact.emailAddress].filter(Boolean).join(' | ')
          : 'Customer contact reference unavailable';
      },
      error: () => this.customerContactDisplayLabel = 'Customer contact reference unavailable'
    });
  }

  private loadCrmClientLabel(crmClientId: number): void {
    this.crmService.getClient(crmClientId).subscribe({
      next: response => {
        this.crmClientDisplayLabel = this.crmClientReferenceLabel(response.data);
      },
      error: () => {
        this.crmClientDisplayLabel = this.firstSafeLinkLabel('CRM_CLIENT') || 'CRM client reference unavailable';
      }
    });
  }

  private loadCrmAccountLabel(crmAccountId: number, crmClientId?: number | null): void {
    const companyId = this.resolveOwningCompanyId();
    if (!companyId) {
      return;
    }
    this.crmService.getAccounts(companyId, crmClientId || undefined).subscribe({
      next: accounts => {
        const account = (accounts || []).find(candidate => candidate.id === crmAccountId);
        this.crmAccountDisplayLabel = this.crmAccountReferenceLabel(account);
      },
      error: () => {
        this.crmAccountDisplayLabel = this.firstSafeLinkLabel('CRM_ACCOUNT') || 'CRM account reference unavailable';
      }
    });
  }

  closeCase(): void {
    if (!this.caseItem?.id || !this.canEditCase) {
      return;
    }
    if (!window.confirm('Close this lifecycle case?')) {
      return;
    }
    this.containerTrackingService.closeCase(this.caseItem.id, {
      closedBy: this.currentUserId()
    }).subscribe({
      next: updated => {
        this.caseItem = updated;
        this.messageService.add({ severity: 'success', summary: 'Closed', detail: 'Container case closed.' });
        this.loadCase();
      },
      error: error => {
        this.messageService.add({
          severity: 'error',
          summary: 'Close blocked',
          detail: error?.error?.message || 'Could not close the container case.'
        });
      }
    });
  }

  openJourneyPlanner(): void {
    if (!this.caseItem?.id) {
      return;
    }
    this.onTabChange(7);
  }

  createJourneyPlan(templateId?: number): void {
    if (!this.caseItem?.id) {
      return;
    }
    this.isSubmitting = true;
    this.containerTrackingService.createJourneyPlan(this.caseItem.id, {
      templateId: templateId || null,
      plannedStartAt: new Date(),
      createdBy: this.currentUserId()
    }).subscribe({
      next: plan => {
        this.isSubmitting = false;
        this.journeyPlans = [plan, ...this.journeyPlans];
        this.selectedJourneyTemplateId = undefined;
        this.messageService.add({
          severity: 'success',
          summary: 'Journey plan created',
          detail: templateId
            ? 'The template was copied into an editable case-specific journey plan.'
            : 'Add transport and storage steps, then confirm them when ready.'
        });
      },
      error: error => {
        this.isSubmitting = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Plan creation failed',
          detail: error?.error?.message || 'Could not create the container journey plan.'
        });
      }
    });
  }

  activeJourneyPlan(): ContainerJourneyPlanView | undefined {
    return this.journeyPlans.find(view => view.plan?.status !== 'CANCELLED') || this.journeyPlans[0];
  }

  journeyItems(): ContainerJourneyPlanItem[] {
    return this.activeJourneyPlan()?.items || [];
  }

  journeyPlanAmendable(): boolean {
    return !['COMPLETED', 'CANCELLED'].includes(
      (this.activeJourneyPlan()?.plan?.status || '').toUpperCase());
  }

  journeyPendingTransportReleases(): ContainerJourneyPlanItem[] {
    return this.journeyItems().filter(item =>
      item.itemType === 'TRANSPORT'
      && (item.status === 'AWAITING_DISPATCH'
        || (!!item.transportOrderReleaseId
          && !this.transportLegs.some(leg => leg.transportReleaseId === item.transportOrderReleaseId))));
  }

  transportContextCount(): number {
    return this.transportLegs.length + this.journeyPendingTransportReleases().length;
  }

  journeyReleaseReference(item: ContainerJourneyPlanItem): string {
    if (!item.transportOrderReleaseId) {
      return 'Not released';
    }
    return this.links.find(link =>
      link.resourceType === 'TRANSPORT_ORDER_RELEASE'
      && link.resourceId === item.transportOrderReleaseId)?.label || 'Order release';
  }

  journeyStorageItems(): ContainerJourneyPlanItem[] {
    return this.journeyItems().filter(item => item.itemType === 'STORAGE');
  }

  journeyPrecedingItem(item: ContainerJourneyPlanItem | undefined = this.editingJourneyItem): ContainerJourneyPlanItem | undefined {
    const items = [...this.journeyItems()].sort((left, right) =>
      (left.sequenceNo || 0) - (right.sequenceNo || 0) || (left.id || 0) - (right.id || 0));
    if (!item) {
      return items[items.length - 1];
    }
    return [...items]
      .reverse()
      .find(candidate => candidate.id !== item.id && (candidate.sequenceNo || 0) < (item.sequenceNo || 0));
  }

  journeyStoragePredecessor(): ContainerJourneyPlanItem | undefined {
    if (this.journeyItemForm?.get('itemType')?.value !== 'STORAGE') {
      return undefined;
    }
    const linkedPredecessorId = this.editingJourneyItem?.predecessorPlanItemId
      || this.journeyItemForm.get('predecessorPlanItemId')?.value;
    return this.journeyItems().find(item => item.id === linkedPredecessorId)
      || this.journeyPrecedingItem();
  }

  journeyTransportPredecessor(): ContainerJourneyPlanItem | undefined {
    if (this.journeyItemForm?.get('itemType')?.value !== 'TRANSPORT') {
      return undefined;
    }
    const linkedPredecessorId = this.editingJourneyItem?.predecessorPlanItemId
      || this.journeyItemForm.get('predecessorPlanItemId')?.value;
    return this.journeyItems().find(item => item.id === linkedPredecessorId)
      || this.journeyPrecedingItem();
  }

  journeyStorageFacilityOptions(): ContainerFacility[] {
    const predecessor = this.journeyStoragePredecessor();
    const inheritedLocationId = this.journeyStepHandoffLocationId(predecessor);
    return this.facilities.filter(facility => facility.active !== false
      && (!inheritedLocationId || facility.locationId === inheritedLocationId));
  }

  journeyStepDestinationLabel(item?: ContainerJourneyPlanItem): string {
    if (!item) {
      return 'Location pending';
    }
    const locationId = this.journeyStepHandoffLocationId(item);
    return (item.itemType === 'TRANSPORT' ? item.destinationLocationText : item.locationText)
      || this.locations.find(location => location.id === locationId)?.name
      || (item.itemType === 'STORAGE'
        ? this.facilities.find(facility => facility.id === item.facilityId)?.facilityName || item.facilityName
        : null)
      || 'Location pending';
  }

  journeyStepLabel(item?: ContainerJourneyPlanItem): string {
    return item ? `Step ${item.sequenceNo || '-'} - ${item.stepName || (item.itemType === 'TRANSPORT' ? 'Transport movement' : 'Storage dwell')}` : 'No preceding step';
  }

  private journeyStepHandoffLocationId(item?: ContainerJourneyPlanItem): number | null {
    if (!item) {
      return null;
    }
    if (item.itemType === 'TRANSPORT') {
      return item.destinationLocationId || null;
    }
    const facilityLocationId = this.facilities.find(facility => facility.id === item.facilityId)?.locationId;
    if (item.locationId || facilityLocationId) {
      return item.locationId || facilityLocationId || null;
    }
    const inboundStep = this.journeyPrecedingItem(item);
    return inboundStep?.itemType === 'TRANSPORT'
      ? inboundStep.destinationLocationId || null
      : inboundStep?.locationId || null;
  }

  openJourneyItemDialog(itemType: 'TRANSPORT' | 'STORAGE', item?: ContainerJourneyPlanItem): void {
    const plan = this.activeJourneyPlan();
    if (!plan?.plan?.id) {
      this.openJourneyPlanner();
      return;
    }
    if (item && !this.journeyItemEditable(item)) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Step already confirmed',
        detail: 'Confirmed journey steps cannot be changed from the planning workspace.'
      });
      return;
    }
    const hasStorageSteps = this.journeyStorageItems().length > 0;
    this.editingJourneyItem = item;
    this.journeyItemForm.get('plannedStartAt')?.enable({ emitEvent: false });
    this.journeyItemForm.get('locationId')?.enable({ emitEvent: false });
    this.journeyItemForm.get('originLocationId')?.enable({ emitEvent: false });
    this.journeyItemForm.get('sourceStoragePlanItemId')?.enable({ emitEvent: false });
    this.journeyItemForm.get('itemType')?.enable({ emitEvent: false });
    this.journeyItemForm.reset({
      itemType: item?.itemType || itemType,
      stepName: item?.stepName || '',
      transportMode: item?.transportMode || 'TRUCK',
      originLocationId: item?.originLocationId || null,
      destinationLocationId: item?.destinationLocationId || null,
      predecessorPlanItemId: item?.predecessorPlanItemId || null,
      locationId: item?.locationId || null,
      locationText: item?.locationText || '',
      facilityType: item?.facilityType || 'PORT_TERMINAL',
      facilityId: item?.facilityId || null,
      storageControlMode: item?.storageControlMode || 'MANAGED',
      storageReason: item?.storageReason || (itemType === 'STORAGE' ? 'TERMINAL_YARD' : null),
      plannedStartAt: this.formDate(item?.plannedStartAt),
      plannedEndAt: this.formDate(item?.plannedEndAt),
      priority: item?.priority || 'NORMAL',
      vehicleTypeId: item?.vehicleTypeId || null,
      routeTemplateId: item?.routeTemplateId || null,
      assignmentRuleId: item?.assignmentRuleId || null,
      manpowerCount: item?.manpowerCount ?? null,
      helperCount: item?.helperCount ?? null,
      thirdPartyEligible: item?.thirdPartyEligible ?? null,
      requiredTools: item?.requiredTools || '',
      sourceStoragePlanItemId: hasStorageSteps ? item?.sourceStoragePlanItemId || null : null,
      destinationStoragePlanItemId: hasStorageSteps ? item?.destinationStoragePlanItemId || null : null,
      remarks: item?.remarks || ''
    });
    this.updateJourneyItemValidators();
    this.applyJourneyStepContinuity(item);
    if (item) {
      this.journeyItemForm.get('itemType')?.disable({ emitEvent: false });
    }
    this.journeyItemDialogVisible = true;
  }

  editJourneyItem(item: ContainerJourneyPlanItem): void {
    this.openJourneyItemDialog(item.itemType || 'TRANSPORT', item);
  }

  closeJourneyItemDialog(): void {
    this.journeyItemDialogVisible = false;
    this.journeyEndMinimum = undefined;
    this.editingJourneyItem = undefined;
    this.journeyItemForm.get('plannedStartAt')?.enable({ emitEvent: false });
    this.journeyItemForm.get('locationId')?.enable({ emitEvent: false });
    this.journeyItemForm.get('originLocationId')?.enable({ emitEvent: false });
    this.journeyItemForm.get('sourceStoragePlanItemId')?.enable({ emitEvent: false });
    this.journeyItemForm.get('itemType')?.enable({ emitEvent: false });
  }

  onJourneyItemTypeChange(): void {
    this.updateJourneyItemValidators();
    this.applyJourneyStepContinuity();
  }

  onJourneyFacilityChange(facilityId?: number | null): void {
    const facility = this.facilities.find(candidate => candidate.id === facilityId);
    const predecessor = this.journeyStoragePredecessor();
    const locationId = this.journeyStepHandoffLocationId(predecessor) || facility?.locationId || null;
    const location = this.locations.find(candidate => candidate.id === locationId);
    this.journeyItemForm.patchValue({
      locationId,
      locationText: location?.name
        || (predecessor ? this.journeyStepDestinationLabel(predecessor) : facility?.facilityName)
        || '',
      facilityType: facility?.facilityType || 'PORT_TERMINAL',
      storageControlMode: facility?.operationMode === 'REFERENCE_ONLY' ? 'REFERENCE_ONLY' : 'MANAGED',
      storageReason: facility?.operationMode === 'REFERENCE_ONLY'
        ? 'THIRD_PARTY_TERMINAL_REFERENCE'
        : this.journeyItemForm.get('storageReason')?.value || 'TERMINAL_YARD'
    }, { emitEvent: false });
  }

  private applyJourneyStepContinuity(item?: ContainerJourneyPlanItem): void {
    const plannedStartControl = this.journeyItemForm.get('plannedStartAt');
    const locationControl = this.journeyItemForm.get('locationId');
    const originControl = this.journeyItemForm.get('originLocationId');
    const sourceStorageControl = this.journeyItemForm.get('sourceStoragePlanItemId');
    plannedStartControl?.enable({ emitEvent: false });
    locationControl?.enable({ emitEvent: false });
    originControl?.enable({ emitEvent: false });
    sourceStorageControl?.enable({ emitEvent: false });
    if (this.journeyItemForm.get('itemType')?.value === 'TRANSPORT') {
      const predecessor = this.journeyPrecedingItem(item);
      if (predecessor) {
        this.journeyItemForm.patchValue({
          predecessorPlanItemId: predecessor.id || null,
          sourceStoragePlanItemId: predecessor.itemType === 'STORAGE' ? predecessor.id || null : null,
          originLocationId: this.journeyStepHandoffLocationId(predecessor),
          plannedStartAt: predecessor.plannedEndAt ? this.formDate(predecessor.plannedEndAt) : null
        }, { emitEvent: false });
        originControl?.disable({ emitEvent: false });
        sourceStorageControl?.disable({ emitEvent: false });
        if (predecessor.plannedEndAt) {
          plannedStartControl?.disable({ emitEvent: false });
        }
      }
      this.syncJourneyEndMinimum();
      return;
    }
    if (this.journeyItemForm.get('itemType')?.value !== 'STORAGE') {
      return;
    }
    const predecessor = this.journeyPrecedingItem(item);
    if (!predecessor) {
      this.onJourneyFacilityChange(this.journeyItemForm.get('facilityId')?.value);
      locationControl?.disable({ emitEvent: false });
      this.syncJourneyEndMinimum();
      return;
    }
    const inheritedLocationId = this.journeyStepHandoffLocationId(predecessor);
    const matchingFacilities = this.facilities.filter(facility => facility.active !== false
      && facility.locationId === inheritedLocationId);
    const requestedFacilityId = item?.facilityId || this.journeyItemForm.get('facilityId')?.value;
    const requestedFacilityMatches = matchingFacilities.some(facility => facility.id === requestedFacilityId);
    const inheritedLocation = this.locations.find(location => location.id === inheritedLocationId);
    this.journeyItemForm.patchValue({
      predecessorPlanItemId: predecessor.id || null,
      locationId: inheritedLocationId,
      locationText: inheritedLocation?.name || this.journeyStepDestinationLabel(predecessor),
      plannedStartAt: predecessor.plannedEndAt ? this.formDate(predecessor.plannedEndAt) : null,
      facilityId: requestedFacilityMatches
        ? requestedFacilityId
        : matchingFacilities.length === 1 ? matchingFacilities[0].id : null
    }, { emitEvent: false });
    this.onJourneyFacilityChange(this.journeyItemForm.get('facilityId')?.value);
    if (predecessor.plannedEndAt) {
      plannedStartControl?.disable({ emitEvent: false });
    }
    locationControl?.disable({ emitEvent: false });
    this.syncJourneyEndMinimum();
  }

  private updateJourneyItemValidators(): void {
    const storageStep = this.journeyItemForm.get('itemType')?.value === 'STORAGE';
    const facilityControl = this.journeyItemForm.get('facilityId');
    const transportModeControl = this.journeyItemForm.get('transportMode');
    const originControl = this.journeyItemForm.get('originLocationId');
    const destinationControl = this.journeyItemForm.get('destinationLocationId');

    facilityControl?.setValidators(storageStep ? [Validators.required] : []);
    transportModeControl?.setValidators(storageStep ? [] : [Validators.required]);
    originControl?.setValidators(storageStep ? [] : [Validators.required]);
    destinationControl?.setValidators(storageStep ? [] : [Validators.required]);
    [facilityControl, transportModeControl, originControl, destinationControl]
      .forEach(control => control?.updateValueAndValidity({ emitEvent: false }));
  }

  private syncJourneyEndMinimum(): void {
    const plannedStart = this.journeyItemForm?.get('plannedStartAt')?.value;
    this.journeyEndMinimum = plannedStart ? new Date(plannedStart) : undefined;
  }

  onJourneyPlannedStartChange(): void {
    const plannedStart = this.journeyItemForm.get('plannedStartAt')?.value;
    const plannedEndControl = this.journeyItemForm.get('plannedEndAt');
    const plannedEnd = plannedEndControl?.value;
    if (plannedStart && plannedEnd && new Date(plannedEnd).getTime() < new Date(plannedStart).getTime()) {
      plannedEndControl?.setValue(new Date(plannedStart));
    }
    this.syncJourneyEndMinimum();
    this.journeyItemForm.updateValueAndValidity();
  }

  private journeyPlannedWindowValidator(control: AbstractControl): ValidationErrors | null {
    const plannedStart = control.get('plannedStartAt')?.value;
    const plannedEnd = control.get('plannedEndAt')?.value;
    if (!plannedStart || !plannedEnd) {
      return null;
    }
    return new Date(plannedEnd).getTime() < new Date(plannedStart).getTime()
      ? { plannedEndBeforeStart: true }
      : null;
  }

  saveJourneyItem(): void {
    const caseId = this.caseItem?.id;
    const planId = this.activeJourneyPlan()?.plan?.id;
    if (!caseId || !planId || this.journeyItemForm.invalid) {
      this.journeyItemForm.markAllAsTouched();
      return;
    }
    const value = this.journeyItemForm.getRawValue();
    const origin = this.locations.find(location => location.id === value.originLocationId);
    const destination = this.locations.find(location => location.id === value.destinationLocationId);
    const facility = this.facilities.find(candidate => candidate.id === value.facilityId);
    const vehicleType = this.vehicleTypes.find(type => type.id === value.vehicleTypeId);
    const routeTemplate = this.routeTemplates.find(template => template.id === value.routeTemplateId);
    const assignmentRule = this.assignmentRules.find(rule => rule.id === value.assignmentRuleId);
    const payload: ContainerJourneyPlanItemUpsertRequest = {
      itemType: value.itemType,
      stepName: value.stepName || (value.itemType === 'TRANSPORT' ? 'Transport step' : 'Storage step'),
      transportMode: value.itemType === 'TRANSPORT' ? value.transportMode : null,
      originLocationId: value.itemType === 'TRANSPORT' ? value.originLocationId : null,
      destinationLocationId: value.itemType === 'TRANSPORT' ? value.destinationLocationId : null,
      originLocationText: origin?.name || null,
      destinationLocationText: destination?.name || null,
      facilityType: value.itemType === 'STORAGE' ? facility?.facilityType || value.facilityType : null,
      facilityId: value.itemType === 'STORAGE' ? value.facilityId : null,
      facilityName: value.itemType === 'STORAGE' ? facility?.facilityName || null : null,
      storageControlMode: value.itemType === 'STORAGE' ? facility?.operationMode === 'REFERENCE_ONLY' ? 'REFERENCE_ONLY' : value.storageControlMode : null,
      operatorCrmClientId: value.itemType === 'STORAGE' ? facility?.operatorCrmClientId || null : null,
      storageReason: value.itemType === 'STORAGE'
        ? (facility?.operationMode === 'REFERENCE_ONLY' ? 'THIRD_PARTY_TERMINAL_REFERENCE' : value.storageReason)
        : null,
      plannedStartAt: value.plannedStartAt,
      plannedEndAt: value.plannedEndAt,
      priority: value.priority,
      vehicleTypeId: value.itemType === 'TRANSPORT' ? value.vehicleTypeId : null,
      vehicleTypeName: vehicleType?.name || null,
      routeTemplateId: value.itemType === 'TRANSPORT' ? value.routeTemplateId : null,
      routeTemplateCode: routeTemplate?.templateCode || null,
      assignmentRuleId: value.itemType === 'TRANSPORT' ? value.assignmentRuleId : null,
      assignmentRuleCode: assignmentRule?.ruleCode || null,
      manpowerCount: this.nullableNumber(value.manpowerCount),
      helperCount: this.nullableNumber(value.helperCount),
      thirdPartyEligible: value.thirdPartyEligible,
      requiredTools: value.requiredTools || null,
      sourceStoragePlanItemId: this.journeyStorageItems().length ? value.sourceStoragePlanItemId || null : null,
      destinationStoragePlanItemId: this.journeyStorageItems().length ? value.destinationStoragePlanItemId || null : null,
      remarks: value.remarks || null
    };
    const editingItemId = this.editingJourneyItem?.id;
    const request = editingItemId
      ? this.containerTrackingService.updateJourneyPlanItem(caseId, planId, editingItemId, payload)
      : this.containerTrackingService.addJourneyPlanItem(caseId, planId, payload);
    this.isSubmitting = true;
    request.subscribe({
      next: () => {
        this.isSubmitting = false;
        this.closeJourneyItemDialog();
        this.messageService.add({
          severity: 'success',
          summary: editingItemId ? 'Step updated' : 'Step added',
          detail: 'The draft journey sequence has been updated.'
        });
        this.loadCase();
      },
      error: error => {
        this.isSubmitting = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Step could not be saved',
          detail: error?.error?.message || 'Review the journey step and try again.'
        });
      }
    });
  }

  journeyItemConfirmable(item: ContainerJourneyPlanItem): boolean {
    return item.status === 'DRAFT' || item.status === 'READY';
  }

  journeyItemEditable(item: ContainerJourneyPlanItem): boolean {
    return this.journeyItemConfirmable(item);
  }

  openJourneyItemDeleteDialog(item: ContainerJourneyPlanItem): void {
    if (!this.journeyItemEditable(item)) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Step already confirmed',
        detail: 'Confirmed journey steps cannot be deleted.'
      });
      return;
    }
    this.journeyItemPendingDelete = item;
    this.journeyItemDeleteDialogVisible = true;
  }

  closeJourneyItemDeleteDialog(): void {
    this.journeyItemDeleteDialogVisible = false;
    this.journeyItemPendingDelete = undefined;
  }

  deleteJourneyItem(): void {
    const caseId = this.caseItem?.id;
    const planId = this.activeJourneyPlan()?.plan?.id;
    const itemId = this.journeyItemPendingDelete?.id;
    if (!caseId || !planId || !itemId) {
      return;
    }
    this.isSubmitting = true;
    this.containerTrackingService.deleteJourneyPlanItem(caseId, planId, itemId).subscribe({
      next: () => {
        this.isSubmitting = false;
        this.closeJourneyItemDeleteDialog();
        this.messageService.add({
          severity: 'success',
          summary: 'Step deleted',
          detail: 'The remaining journey steps have been resequenced.'
        });
        this.loadCase();
      },
      error: error => {
        this.isSubmitting = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Step could not be deleted',
          detail: error?.error?.message || 'Only unconfirmed journey steps can be deleted.'
        });
      }
    });
  }

  confirmJourneyItem(item: ContainerJourneyPlanItem): void {
    const caseId = this.caseItem?.id;
    const planId = this.activeJourneyPlan()?.plan?.id;
    if (!caseId || !planId || !item.id) {
      return;
    }
    if (!this.journeyItemConfirmable(item)) {
      this.messageService.add({
        severity: 'info',
        summary: 'Step already confirmed',
        detail: 'This journey step has already been released downstream.'
      });
      return;
    }
    this.isSubmitting = true;
    this.containerTrackingService.confirmJourneyPlanItem(caseId, planId, item.id, this.currentUserId()).subscribe({
      next: updated => {
        this.isSubmitting = false;
        this.journeyPlans = [updated, ...this.journeyPlans.filter(view => view.plan?.id !== updated.plan?.id)];
        this.messageService.add({
          severity: 'success',
          summary: 'Journey step confirmed',
          detail: item.itemType === 'TRANSPORT'
            ? (this.transportModeOptions.some(mode => mode.value === item.transportMode)
              ? 'This container is ready to be grouped into a shipment or sent to transport planning.'
              : 'The order release was created and sent to transport planning.')
            : 'The planned storage leg was created.'
        });
        this.loadCase();
      },
      error: error => {
        this.isSubmitting = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Confirmation blocked',
          detail: error?.error?.message || 'The selected journey steps are not ready.'
        });
      }
    });
  }

  openSaveJourneyTemplateDialog(): void {
    if (!this.activeJourneyPlan()?.plan?.id) {
      return;
    }
    this.journeyTemplateForm.reset({
      templateCode: '',
      templateName: '',
      description: '',
      journeyType: '',
      applicableEquipmentTypes: this.caseItem?.isoEquipmentType || ''
    });
    this.journeyTemplateDialogVisible = true;
  }

  saveJourneyAsTemplate(): void {
    const caseId = this.caseItem?.id;
    const planId = this.activeJourneyPlan()?.plan?.id;
    if (!caseId || !planId || this.journeyTemplateForm.invalid) {
      this.journeyTemplateForm.markAllAsTouched();
      return;
    }
    this.isSubmitting = true;
    this.containerTrackingService.saveJourneyPlanAsTemplate(caseId, planId, {
      ...this.journeyTemplateForm.getRawValue(),
      createdBy: this.currentUserId()
    }).subscribe({
      next: template => {
        this.isSubmitting = false;
        this.journeyTemplateDialogVisible = false;
        this.journeyTemplates = [template, ...this.journeyTemplates];
        this.messageService.add({ severity: 'success', summary: 'Template saved', detail: 'This journey can now be reused on other container cases.' });
      },
      error: error => {
        this.isSubmitting = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Template could not be saved',
          detail: error?.error?.message || 'Review the template details and try again.'
        });
      }
    });
  }

  journeyStepSummary(item: ContainerJourneyPlanItem): string {
    if (item.itemType === 'TRANSPORT') {
      return `${item.originLocationText || 'Origin pending'} to ${item.destinationLocationText || 'Destination pending'}`;
    }
    return item.facilityName || 'Facility pending';
  }

  journeyStepOwnership(item: ContainerJourneyPlanItem): string {
    if (item.shipmentDeliveryId) {
      return 'Shipment execution linked';
    }
    if (item.status === 'AWAITING_DISPATCH') {
      return 'Ready for shipment dispatch';
    }
    if (item.transportOrderReleaseId) {
      return 'Released to transport planning';
    }
    if (item.containerStorageLegId) {
      return 'Planned storage leg created';
    }
    return 'Journey planning';
  }

  openDispatchDialog(item: ContainerJourneyPlanItem): void {
    if (!item.id || item.status !== 'AWAITING_DISPATCH' || !this.caseItem?.owningCompanyId) return;
    this.isSubmitting = true;
    this.dispatchItem = item;
    this.dispatchPricingSequence++;
    this.dispatchRateLoading = false;
    this.dispatchError = '';
    this.dispatchCurrency = 'SAR';
    this.dispatchRateSource = 'RATE_CARD';
    this.dispatchSellingPrice = null;
    this.dispatchPaymentTermId = undefined;
    this.dispatchContract = undefined;
    this.dispatchRevisionId = undefined;
    this.dispatchRateCard = undefined;
    this.dispatchContracts = [];
    this.dispatchRateCards = [];
    this.dispatchOverrideReason = '';
    this.dispatchPricingWarnings = [];
    this.dispatchCanViewPricing = this.permissionService.hasPermission(app_permissions.shipments.pricing.view);
    const client = this.caseItem.crmClientId
      ? this.crmService.getClient(this.caseItem.crmClientId).pipe(catchError(() => of(null))) : of(null);
    forkJoin({
      candidates: this.containerTrackingService.findTransportDispatchCandidates(item.id),
      terms: this.ordersService.getAllPaymentTerms(this.caseItem.owningCompanyId),
      client
    }).subscribe({
      next: ({ candidates, terms, client }) => {
        this.isSubmitting = false;
        this.dispatchPaymentTerms = (terms || []).filter(term => term.active !== false);
        const defaultId = client?.data?.defaultPaymentTermId;
        this.dispatchPaymentTermId = this.dispatchPaymentTerms.find(term => term.paymentTermID === defaultId)?.paymentTermID
          ?? this.dispatchPaymentTerms[0]?.paymentTermID;
        this.dispatchCandidates = candidates;
        this.selectedDispatchCandidates = candidates.filter(candidate => candidate.journeyItemId === item.id);
        this.dispatchDialogVisible = true;
        if (this.dispatchCanViewPricing) { this.loadDispatchContracts(); this.previewDispatchRate(); }
      },
      error: error => {
        this.isSubmitting = false;
        this.messageService.add({ severity: 'error', summary: 'Cannot load shipment details',
          detail: error?.error?.message || 'Try again.' });
      }
    });
  }

  loadDispatchContracts(event?: { first?: number; rows?: number }): void {
    if (this.dispatchContractsLoading) return;
    this.dispatchContractsLoading = true;
    const companyId = this.caseItem?.owningCompanyId;
    const clientId = this.caseItem?.crmClientId;
    const rows = event?.rows ?? 50;
    const page = Math.floor((event?.first ?? 0) / rows);
    this.contractsService.getAllContracts(page, rows, clientId ? undefined : companyId,
      clientId ? undefined : this.caseItem?.customerCompanyId, clientId).subscribe({
      next: result => {
        const options = (result.items || []).filter(contract => !clientId || contract.firstParty?.id === companyId);
        this.dispatchContracts = [...this.dispatchContracts, ...options.filter(option =>
          !this.dispatchContracts.some(existing => existing.contractID === option.contractID))];
        this.dispatchContractsLoading = false;
      },
      error: () => {
        this.dispatchContractsLoading = false;
        this.dispatchPricingWarnings = ['Could not load customer contracts.'];
      }
    });
  }

  dispatchContractChanged(): void {
    const pickup = Math.max(...this.selectedDispatchCandidates.map(candidate => new Date(candidate.plannedStartAt).getTime()));
    this.dispatchRevisionId = [...(this.dispatchContract?.revisions || [])]
      .filter(revision => String(revision.status) === 'ACTIVE')
      .filter(revision => (!revision.effectiveStartDate || new Date(revision.effectiveStartDate).getTime() <= pickup)
        && (!revision.endDate || new Date(revision.endDate).getTime() >= pickup))
      .sort((a, b) => b.revisionID - a.revisionID)[0]?.revisionID
      ?? this.dispatchContract?.revisions?.[0]?.revisionID;
    this.dispatchRevisionChanged();
  }

  dispatchPricingContextChanged(): void {
    this.dispatchContract = undefined;
    this.dispatchRevisionId = undefined;
    this.dispatchRateCard = undefined;
    this.dispatchRateCards = [];
    if (this.dispatchRateSource === 'RATE_CARD') this.dispatchSellingPrice = null;
    this.previewDispatchRate();
  }

  dispatchRevisionChanged(): void {
    this.dispatchRateCard = undefined;
    this.dispatchRateCards = [];
    if (this.dispatchContract?.contractID && this.dispatchRevisionId) {
      this.contractsService.getDeliveryRateCardsByRevisionId(this.dispatchContract.contractID, this.dispatchRevisionId)
        .subscribe({ next: cards => this.dispatchRateCards = cards || [],
          error: () => this.dispatchPricingWarnings = ['Could not load rate cards.'] });
    }
    this.previewDispatchRate();
  }

  dispatchSourceChanged(): void {
    if (this.dispatchRateSource === 'MANUAL') {
      this.dispatchPricingSequence++;
      this.dispatchRateLoading = false;
      this.dispatchContract = undefined;
      this.dispatchRevisionId = undefined;
      this.dispatchRateCard = undefined;
      this.dispatchPricingWarnings = [];
    } else {
      this.previewDispatchRate();
    }
  }

  previewDispatchRate(): void {
    const sequence = ++this.dispatchPricingSequence;
    this.dispatchRateLoading = false;
    if (!this.dispatchCanViewPricing || this.dispatchRateSource !== 'RATE_CARD' || !this.dispatchSelectionCompatible()) return;
    this.dispatchRateLoading = true;
    this.dispatchSellingPrice = null;
    const vehicleTypes = new Set(this.selectedDispatchCandidates.map(candidate => candidate.vehicleTypeId ?? null));
    const vehicleTypeId = vehicleTypes.size === 1 ? [...vehicleTypes][0] ?? undefined : undefined;
    const pickup = Math.max(...this.selectedDispatchCandidates.map(candidate => new Date(candidate.plannedStartAt).getTime()));
    this.contractsService.decideRateCard({
      sellingCompanyId: this.caseItem?.owningCompanyId,
      buyingCompanyId: this.caseItem?.crmClientId ? undefined : this.caseItem?.customerCompanyId,
      crmClientId: this.caseItem?.crmClientId, crmAccountId: this.caseItem?.crmAccountId,
      sourceLocationId: this.dispatchItem?.originLocationId ?? undefined,
      destinationLocationId: this.dispatchItem?.destinationLocationId ?? undefined,
      pickupWindowStart: pickup, vehicleTypeId,
      contractId: this.dispatchContract?.contractID, contractRevisionId: this.dispatchRevisionId,
      rateCardId: this.dispatchRateCard?.rateId, currencyCode: this.dispatchCurrency
    }).subscribe({
      next: decision => {
        if (sequence !== this.dispatchPricingSequence) return;
        this.dispatchRateLoading = false;
        this.dispatchPricingWarnings = decision.warnings || [];
        this.dispatchSellingPrice = decision.matched ? decision.sellingPrice : null;
        if (decision.matched && decision.rateCard) {
          this.dispatchRateCard = decision.rateCard;
          this.dispatchRevisionId = decision.contractRevisionId;
          this.dispatchRateCards = [decision.rateCard, ...this.dispatchRateCards.filter(card => card.rateId !== decision.rateCardId)];
          if (decision.contractId && this.dispatchContract?.contractID !== decision.contractId) {
            this.contractsService.getContractById(decision.contractId).subscribe({ next: contract => {
              if (sequence !== this.dispatchPricingSequence) return;
              this.dispatchContract = contract;
              if (!this.dispatchContracts.some(option => option.contractID === contract.contractID)) this.dispatchContracts.push(contract);
            }});
          }
        }
      },
      error: () => {
        if (sequence === this.dispatchPricingSequence) {
          this.dispatchRateLoading = false;
          this.dispatchPricingWarnings = ['Could not preview the customer rate.'];
        }
      }
    });
  }

  createGroupedShipment(): void {
    if (!this.dispatchSelectionCompatible() || !this.dispatchPaymentTermId || this.dispatchRateLoading) return;
    if (this.dispatchSellingPrice != null && (!Number.isFinite(this.dispatchSellingPrice) || this.dispatchSellingPrice < 0)) {
      this.dispatchError = 'Selling price must be a non-negative amount.';
      return;
    }
    const noPricingSelected = this.dispatchRateSource === 'RATE_CARD' && this.dispatchSellingPrice == null
      && !this.dispatchContract && !this.dispatchRevisionId && !this.dispatchRateCard;
    const pricing: ShipmentIntakePricing | undefined = this.dispatchCanViewPricing && !noPricingSelected ? {
      currencyCode: this.dispatchCurrency, rateSource: this.dispatchRateSource,
      contractId: this.dispatchContract?.contractID, contractRevisionId: this.dispatchRevisionId,
      rateCardId: this.dispatchRateCard?.rateId, sellingPrice: this.dispatchSellingPrice ?? undefined,
      overrideReason: this.dispatchOverrideReason.trim() || undefined
    } : undefined;
    this.dispatchError = '';
    this.isSubmitting = true;
    this.containerTrackingService.createTransportDispatch(
      this.selectedDispatchCandidates.map(candidate => candidate.journeyItemId), this.dispatchPaymentTermId, pricing).subscribe({
      next: result => {
        this.isSubmitting = false;
        this.dispatchDialogVisible = false;
        this.messageService.add({ severity: 'success', summary: 'Shipment created',
          detail: result.shipmentNumber + ' contains ' + result.containerCaseIds.length + ' container case(s).' });
        this.loadCase();
        this.router.navigate(['/shipment/delivery/view', result.deliveryId]);
      },
      error: error => {
        this.isSubmitting = false;
        this.dispatchError = error?.error?.message || 'Review the selected cases and try again.';
      }
    });
  }

  dispatchSelectionCompatible(): boolean {
    const selected = this.selectedDispatchCandidates;
    return selected.length > 0
      && new Set(selected.map(candidate => candidate.containerCaseId)).size === selected.length
      && Math.max(...selected.map(candidate => new Date(candidate.plannedStartAt).getTime()))
        <= Math.min(...selected.map(candidate => new Date(candidate.plannedEndAt).getTime()));
  }

  sendJourneyItemToPlanning(item: ContainerJourneyPlanItem): void {
    const caseId = this.caseItem?.id;
    const planId = this.activeJourneyPlan()?.plan?.id;
    if (!caseId || !planId || !item.id) return;
    this.isSubmitting = true;
    this.containerTrackingService.sendJourneyItemToPlanning(caseId, planId, item.id).subscribe({
      next: updated => {
        this.isSubmitting = false;
        this.journeyPlans = [updated, ...this.journeyPlans.filter(view => view.plan?.id !== updated.plan?.id)];
        this.messageService.add({ severity: 'success', summary: 'Sent to transport planning',
          detail: 'The order release is ready for the optimizer.' });
        this.loadCase();
      },
      error: error => {
        this.isSubmitting = false;
        this.messageService.add({ severity: 'error', summary: 'Planning request failed',
          detail: error?.error?.message || 'Try again.' });
      }
    });
  }

  openAddStorageDialog(): void {
    this.storageForm.reset({
      facilityType: 'BONDED_YARD',
      facilityId: null,
      facilityName: '',
      warehouseId: null,
      zoneId: null,
      yardAreaId: null,
      yardSlotId: null,
      slotCode: '',
      inboundTransportLegId: this.activeTransportLeg()?.id || null,
      outboundTransportLegId: null,
      storageReason: 'TERMINAL_YARD',
      plannedInAt: null,
      plannedOutAt: null,
      freeTimeStartAt: null,
      freeTimeEndAt: null,
      chargeableFromAt: null,
      chargeableToAt: null,
      storageControlMode: 'MANAGED',
      operatorCrmClientId: null,
      sourceReference: '',
      notes: ''
    });
    this.storageDialogVisible = true;
  }

  onStorageFacilityChange(facilityId?: number | null): void {
    const facility = this.facilities.find(item => item.id === facilityId);
    this.storageForm.patchValue({
      facilityType: facility?.facilityType || this.storageForm.get('facilityType')?.value,
      facilityName: facility?.facilityName || '',
      storageControlMode: facility?.operationMode === 'REFERENCE_ONLY' ? 'REFERENCE_ONLY' : 'MANAGED',
      operatorCrmClientId: facility?.operatorCrmClientId || null,
      warehouseId: facility?.linkedWarehouseId || null,
      zoneId: null,
      yardAreaId: null,
      yardSlotId: null,
      slotCode: ''
    }, { emitEvent: false });
    if (facility?.operationMode === 'REFERENCE_ONLY') {
      this.storageForm.patchValue({
        warehouseId: null,
        zoneId: null,
        yardAreaId: null,
        yardSlotId: null,
        slotCode: '',
        freeTimeStartAt: null,
        freeTimeEndAt: null,
        chargeableFromAt: null,
        chargeableToAt: null,
        storageReason: 'THIRD_PARTY_TERMINAL_REFERENCE'
      }, { emitEvent: false });
    }
  }

  onStorageFacilityTypeChange(): void {
    this.storageForm.patchValue({
      facilityId: null,
      facilityName: '',
      storageControlMode: 'MANAGED',
      operatorCrmClientId: null,
      warehouseId: null,
      zoneId: null,
      yardAreaId: null,
      yardSlotId: null,
      slotCode: ''
    }, { emitEvent: false });
  }

  onStorageAreaChange(areaId?: number | null): void {
    const slotId = this.storageForm.get('yardSlotId')?.value;
    const slot = this.yardSlots.find(item => item.id === slotId);
    if (slot && areaId && slot.areaId !== areaId) {
      this.storageForm.patchValue({ yardSlotId: null, slotCode: '' }, { emitEvent: false });
    }
  }

  onStorageSlotChange(slotId?: number | null): void {
    const slot = this.yardSlots.find(item => item.id === slotId);
    this.storageForm.patchValue({
      yardAreaId: slot?.areaId || this.storageForm.get('yardAreaId')?.value || null,
      slotCode: slot?.slotCode || ''
    }, { emitEvent: false });
  }

  saveStorageLeg(): void {
    if (!this.caseItem?.id || this.storageForm.invalid) {
      this.storageForm.markAllAsTouched();
      return;
    }
    this.isSubmitting = true;
    const formValue = this.storageForm.getRawValue();
    const facility = this.facilities.find(item => item.id === formValue.facilityId);
    const slot = this.yardSlots.find(item => item.id === formValue.yardSlotId);
    const referenceOnly = facility?.operationMode === 'REFERENCE_ONLY' || formValue.storageControlMode === 'REFERENCE_ONLY';
    this.containerTrackingService.createStorageLeg(this.caseItem.id, {
      facilityType: formValue.facilityType,
      facilityId: formValue.facilityId || undefined,
      facilityName: facility?.facilityName || formValue.facilityName || undefined,
      storageControlMode: referenceOnly ? 'REFERENCE_ONLY' : 'MANAGED',
      operatorCrmClientId: facility?.operatorCrmClientId || formValue.operatorCrmClientId || undefined,
      warehouseId: referenceOnly ? undefined : formValue.warehouseId || undefined,
      zoneId: referenceOnly ? undefined : formValue.zoneId || undefined,
      yardAreaId: referenceOnly ? undefined : formValue.yardAreaId || undefined,
      yardSlotId: referenceOnly ? undefined : formValue.yardSlotId || undefined,
      slotCode: referenceOnly ? undefined : slot?.slotCode || formValue.slotCode || undefined,
      inboundTransportLegId: formValue.inboundTransportLegId || undefined,
      outboundTransportLegId: formValue.outboundTransportLegId || undefined,
      storageReason: referenceOnly ? 'THIRD_PARTY_TERMINAL_REFERENCE' : formValue.storageReason || undefined,
      plannedInAt: formValue.plannedInAt || undefined,
      plannedOutAt: formValue.plannedOutAt || undefined,
      freeTimeStartAt: referenceOnly ? undefined : formValue.freeTimeStartAt || undefined,
      freeTimeEndAt: referenceOnly ? undefined : formValue.freeTimeEndAt || undefined,
      chargeableFromAt: referenceOnly ? undefined : formValue.chargeableFromAt || undefined,
      chargeableToAt: referenceOnly ? undefined : formValue.chargeableToAt || undefined,
      sourceModule: 'CONTAINER_TRACKING',
      sourceReference: formValue.sourceReference || undefined,
      notes: formValue.notes || undefined,
      status: 'PLANNED',
      createdBy: this.currentUserId()
    }).subscribe({
      next: () => {
        this.isSubmitting = false;
        this.storageDialogVisible = false;
        this.messageService.add({ severity: 'success', summary: 'Saved', detail: 'Storage leg added.' });
        this.loadCase();
      },
      error: error => {
        this.isSubmitting = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Save failed',
          detail: error?.error?.message || 'Could not add storage leg.'
        });
      }
    });
  }

  exceptionTransportLeg(leg: ContainerTransportLeg): void {
    const exceptionReason = window.prompt('Exception reason', leg.exceptionReason || '');
    if (!exceptionReason) {
      return;
    }
    this.executeTransportAction('exception', leg, () => this.containerTrackingService.exceptionTransportLeg(this.caseItem!.id!, leg.id!, {
      eventTime: new Date(),
      exceptionReason,
      remarks: exceptionReason,
      updatedBy: this.currentUserId()
    }));
  }

  receiveStorageLeg(leg: ContainerStorageLeg): void {
    this.openHandoffDialog('receiveStorage', undefined, leg);
  }

  slotStorageLeg(leg: ContainerStorageLeg): void {
    this.selectedStorageLeg = leg;
    this.storageSlotForm.reset({
      yardAreaId: leg.yardAreaId || null,
      yardSlotId: leg.yardSlotId || null,
      slotCode: leg.slotCode || '',
      notes: leg.notes || ''
    });
    this.storageSlotDialogVisible = true;
  }

  saveStorageSlotAssignment(): void {
    if (!this.caseItem?.id || !this.selectedStorageLeg?.id) {
      return;
    }
    const value = this.storageSlotForm.getRawValue();
    const selectedSlot = this.yardSlots.find(slot => slot.id === value.yardSlotId);
    const slotCode = selectedSlot?.slotCode || value.slotCode;
    if (!slotCode) {
      this.messageService.add({ severity: 'warn', summary: 'Slot required', detail: 'Select a yard slot or enter a slot code.' });
      return;
    }
    this.isSubmitting = true;
    this.containerTrackingService.assignStorageSlot(this.caseItem.id, this.selectedStorageLeg.id, {
      zoneId: this.selectedStorageLeg.zoneId || undefined,
      slotCode,
      notes: value.notes || undefined,
      updatedBy: this.currentUserId()
    }).subscribe({
      next: () => {
        this.isSubmitting = false;
        this.storageSlotDialogVisible = false;
        this.selectedStorageLeg = undefined;
        this.messageService.add({ severity: 'success', summary: 'Slot assigned', detail: 'Storage slot updated.' });
        this.loadCase();
      },
      error: error => {
        this.isSubmitting = false;
        this.messageService.add({ severity: 'error', summary: 'Slot failed', detail: error?.error?.message || 'Could not assign storage slot.' });
      }
    });
  }

  holdStorageLeg(leg: ContainerStorageLeg): void {
    this.selectedStorageLeg = leg;
    this.storageHoldForm.reset({
      eventTime: new Date(),
      notes: leg.notes || ''
    });
    this.storageHoldDialogVisible = true;
  }

  saveStorageHold(): void {
    if (!this.caseItem?.id || !this.selectedStorageLeg?.id) {
      return;
    }
    const value = this.storageHoldForm.getRawValue();
    this.isSubmitting = true;
    this.containerTrackingService.holdStorageLeg(this.caseItem.id, this.selectedStorageLeg.id, {
      eventTime: value.eventTime || new Date(),
      notes: value.notes || undefined,
      updatedBy: this.currentUserId()
    }).subscribe({
      next: () => {
        this.isSubmitting = false;
        this.storageHoldDialogVisible = false;
        this.selectedStorageLeg = undefined;
        this.messageService.add({ severity: 'success', summary: 'Hold applied', detail: 'Storage hold was recorded.' });
        this.loadCase();
      },
      error: error => {
        this.isSubmitting = false;
        this.messageService.add({ severity: 'error', summary: 'Hold failed', detail: error?.error?.message || 'Could not apply storage hold.' });
      }
    });
  }

  releaseStorageLeg(leg: ContainerStorageLeg): void {
    this.selectedStorageLeg = leg;
    this.storageReleaseForm.reset({
      eventTime: new Date(),
      notes: ''
    });
    this.storageReleaseDialogVisible = true;
  }

  saveStorageRelease(): void {
    if (!this.caseItem?.id || !this.selectedStorageLeg?.id) {
      return;
    }
    const value = this.storageReleaseForm.getRawValue();
    this.isSubmitting = true;
    this.containerTrackingService.releaseStorageLeg(this.caseItem.id, this.selectedStorageLeg.id, {
      eventTime: value.eventTime || new Date(),
      notes: value.notes || undefined,
      updatedBy: this.currentUserId()
    }).subscribe({
      next: () => {
        this.isSubmitting = false;
        this.storageReleaseDialogVisible = false;
        this.selectedStorageLeg = undefined;
        this.messageService.add({ severity: 'success', summary: 'Hold released', detail: 'Storage hold was released.' });
        this.loadCase();
      },
      error: error => {
        this.isSubmitting = false;
        this.messageService.add({ severity: 'error', summary: 'Release failed', detail: error?.error?.message || 'Could not release storage hold.' });
      }
    });
  }

  exitStorageLeg(leg: ContainerStorageLeg): void {
    this.openHandoffDialog('exitStorage', undefined, leg);
  }

  reconcileHandoffs(): void {
    if (!this.caseItem?.id) {
      return;
    }
    this.isSubmitting = true;
    this.containerTrackingService.reconcileHandoffs(this.caseItem.id).subscribe({
      next: handoffs => {
        this.handoffs = handoffs;
        this.isSubmitting = false;
        this.messageService.add({ severity: 'success', summary: 'Reconciled', detail: 'Handoffs were reconciled.' });
        this.loadCase();
      },
      error: error => {
        this.isSubmitting = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Reconcile failed',
          detail: error?.error?.message || 'Could not reconcile handoffs.'
        });
      }
    });
  }

  openYardGateInDialog(): void {
    const activeTransportLeg = this.activeTransportLeg();
    this.yardGateInForm.reset({
      facilityId: this.storageLegs.find(leg => leg.facilityId)?.facilityId || null,
      appointmentId: null,
      initialAreaId: null,
      initialSlotId: null,
      inboundTransportLegId: activeTransportLeg?.id || null,
      gateInAt: new Date(),
      freeTimeStartAt: null,
      freeTimeEndAt: null,
      emptyFullStatus: 'UNKNOWN',
      sealVerified: true,
      sealMismatchReason: '',
      conditionStatus: 'UNKNOWN',
      damageObserved: false,
      damageNotes: '',
      truckPlateNumber: '',
      chassisNumber: '',
      remarks: ''
    });
    this.yardGateInDialogVisible = true;
  }

  saveYardGateIn(): void {
    if (!this.caseItem?.id || !this.caseItem.owningCompanyId || this.yardGateInForm.invalid) {
      this.yardGateInForm.markAllAsTouched();
      return;
    }
    const value = this.yardGateInForm.getRawValue();
    this.isSubmitting = true;
    this.terminalYardService.gateIn({
      owningCompanyId: this.caseItem.owningCompanyId,
      facilityId: value.facilityId,
      containerCaseId: this.caseItem.id,
      containerNumber: this.caseItem.containerNumber,
      isoEquipmentType: this.caseItem.isoEquipmentType,
      sealNumber: this.caseItem.sealNumber,
      carrierName: value.carrierName,
      truckPlateNumber: value.truckPlateNumber,
      chassisNumber: value.chassisNumber,
      billOfLadingNumber: this.caseItem.billOfLadingNumber,
      bookingNumber: this.caseItem.bookingNumber,
      visitType: 'TERMINAL_YARD',
      logisticsProjectId: this.caseItem.logisticsProjectId,
      customerCompanyId: this.caseItem.customerCompanyId,
      crmClientId: this.caseItem.crmClientId,
      crmAccountId: this.caseItem.crmAccountId,
      appointmentId: value.appointmentId || undefined,
      initialAreaId: value.initialAreaId || undefined,
      initialSlotId: value.initialSlotId || undefined,
      inboundTransportLegId: value.inboundTransportLegId || undefined,
      gateInAt: value.gateInAt || new Date(),
      freeTimeStartAt: value.freeTimeStartAt || undefined,
      freeTimeEndAt: value.freeTimeEndAt || undefined,
      emptyFullStatus: value.emptyFullStatus || undefined,
      sealVerified: value.sealVerified,
      sealMismatchReason: value.sealMismatchReason || undefined,
      conditionStatus: value.conditionStatus || undefined,
      damageObserved: value.damageObserved,
      damageNotes: value.damageNotes || undefined,
      createdBy: this.currentUserId(),
      remarks: value.remarks || undefined
    }).subscribe({
      next: () => {
        this.isSubmitting = false;
        this.yardGateInDialogVisible = false;
        this.messageService.add({ severity: 'success', summary: 'Gate-in recorded', detail: 'Terminal yard visit was created.' });
        this.loadCase();
      },
      error: error => {
        this.isSubmitting = false;
        this.messageService.add({ severity: 'error', summary: 'Gate-in failed', detail: error?.error?.message || 'Could not gate in this container.' });
      }
    });
  }

  openYardGateOutDialog(visit: ContainerYardVisit): void {
    this.selectedYardVisit = visit;
    this.yardGateOutForm.reset({
      gateOutAt: new Date(),
      emptyReturn: false,
      outboundTransportLegId: this.nextPlannedTransportLeg()?.id || null,
      referenceNumber: '',
      sealVerified: visit.sealVerified ?? true,
      sealMismatchReason: visit.sealMismatchReason || '',
      conditionStatus: visit.conditionStatus || 'UNKNOWN',
      damageObserved: !!visit.damageObserved,
      damageNotes: visit.damageNotes || '',
      remarks: ''
    });
    this.yardGateOutDialogVisible = true;
  }

  saveYardGateOut(): void {
    if (!this.caseItem?.owningCompanyId || !this.selectedYardVisit?.id) {
      return;
    }
    const value = this.yardGateOutForm.getRawValue();
    this.isSubmitting = true;
    this.terminalYardService.gateOut(this.selectedYardVisit.id, {
      owningCompanyId: this.caseItem.owningCompanyId,
      gateOutAt: value.gateOutAt || new Date(),
      emptyReturn: !!value.emptyReturn,
      outboundTransportLegId: value.outboundTransportLegId || undefined,
      referenceNumber: value.referenceNumber || undefined,
      sealVerified: value.sealVerified,
      sealMismatchReason: value.sealMismatchReason || undefined,
      conditionStatus: value.conditionStatus || undefined,
      damageObserved: value.damageObserved,
      damageNotes: value.damageNotes || undefined,
      remarks: value.remarks || undefined,
      createdBy: this.currentUserId()
    }).subscribe({
      next: () => {
        this.isSubmitting = false;
        this.yardGateOutDialogVisible = false;
        this.selectedYardVisit = undefined;
        this.messageService.add({ severity: 'success', summary: 'Gate-out recorded', detail: 'Terminal yard visit was closed.' });
        this.loadCase();
      },
      error: error => {
        this.isSubmitting = false;
        this.messageService.add({ severity: 'error', summary: 'Gate-out failed', detail: error?.error?.message || 'Could not gate out this container.' });
      }
    });
  }

  openYardMoveDialog(visit: ContainerYardVisit, moveType = 'YARD_MOVE'): void {
    this.selectedYardVisit = visit;
    this.yardMoveForm.reset({
      moveType,
      toAreaId: visit.currentAreaId || null,
      toSlotId: null,
      eventTime: new Date(),
      referenceNumber: '',
      remarks: ''
    });
    this.yardMoveDialogVisible = true;
  }

  saveYardMove(): void {
    if (!this.caseItem?.owningCompanyId || !this.selectedYardVisit?.id || this.yardMoveForm.invalid) {
      this.yardMoveForm.markAllAsTouched();
      return;
    }
    const value = this.yardMoveForm.getRawValue();
    this.isSubmitting = true;
    this.terminalYardService.move(this.selectedYardVisit.id, {
      owningCompanyId: this.caseItem.owningCompanyId,
      moveType: value.moveType,
      toAreaId: value.toAreaId || undefined,
      toSlotId: value.toSlotId || undefined,
      eventTime: value.eventTime || new Date(),
      referenceNumber: value.referenceNumber || undefined,
      remarks: value.remarks || undefined,
      createdBy: this.currentUserId()
    }).subscribe({
      next: () => {
        this.isSubmitting = false;
        this.yardMoveDialogVisible = false;
        this.selectedYardVisit = undefined;
        this.messageService.add({ severity: 'success', summary: 'Move recorded', detail: 'Yard move was added to the ledger.' });
        this.loadCase();
      },
      error: error => {
        this.isSubmitting = false;
        this.messageService.add({ severity: 'error', summary: 'Move failed', detail: error?.error?.message || 'Could not record yard move.' });
      }
    });
  }

  navigateToLinkedResource(link: ContainerCaseResourceLink): void {
    const route = this.routeForLink(link);
    if (!route) {
      return;
    }
    this.router.navigate(route.commands, route.extras || undefined);
  }

  openTransportRelease(releaseId?: number | null): void {
    if (releaseId) {
      this.router.navigate(['/transport-planning/releases', releaseId]);
    }
  }

  openShipmentDelivery(leg: ContainerTransportLeg): void {
    if (leg.shipmentDeliveryId) {
      this.router.navigate(['/shipment/delivery/view', leg.shipmentDeliveryId]);
    } else if (leg.executionLinkId) {
      this.router.navigate(['/transport-planning'], { queryParams: { executionLinkId: leg.executionLinkId } });
    }
  }

  openJourneyShipmentDelivery(item: ContainerJourneyPlanItem): void {
    if (item.shipmentDeliveryId) {
      this.router.navigate(['/shipment/delivery/view', item.shipmentDeliveryId]);
    }
  }

  openStorageControl(leg: ContainerStorageLeg): void {
    if (this.caseItem?.id && leg.id) {
      this.router.navigate(['/container-tracking/cases/view', this.caseItem.id, 'storage-legs', leg.id]);
    }
  }

  routeForLink(link: ContainerCaseResourceLink): { commands: any[]; extras?: any } | null {
    if (!link.resourceId) {
      return null;
    }
    switch (link.resourceType) {
      case 'SHIPMENT':
        return { commands: ['/shipment/view', link.resourceId] };
      case 'SHIPMENT_DELIVERY':
        return { commands: ['/shipment/delivery/view', link.resourceId] };
      case 'TRANSPORT_ORDER_RELEASE':
        return { commands: ['/transport-planning/releases', link.resourceId] };
      case 'TRANSPORT_EXECUTION_LINK':
        return { commands: ['/transport-planning'], extras: { queryParams: { executionLinkId: link.resourceId } } };
      case 'CONTAINER_YARD_VISIT':
        return { commands: ['/container-tracking/setup'], extras: { queryParams: { tab: 'terminal-yard', visitId: link.resourceId } } };
      case 'CONTAINER_YARD_MOVE':
        return { commands: ['/container-tracking/setup'], extras: { queryParams: { tab: 'terminal-yard', moveId: link.resourceId } } };
      case 'CONTAINER_LEG_HANDOFF':
        return this.caseItem?.id
          ? { commands: ['/container-tracking/cases/view', this.caseItem.id], extras: { queryParams: { tab: 'handoffs', handoffId: link.resourceId } } }
          : null;
      case 'CLEARANCE_HEADER':
        return { commands: ['/clearance/view', link.resourceId] };
      case 'PURCHASE_ORDER':
        return { commands: ['/order/purchase-order/view', link.resourceId] };
      case 'PURCHASE_ORDER_RECEIPT':
        return { commands: ['/warehousing/quality'], extras: { queryParams: { purchaseOrderReceiptId: link.resourceId } } };
      case 'WAREHOUSE_STOCK_UNIT':
        return { commands: ['/warehousing/stock/view', link.resourceId] };
      case 'WAREHOUSE_STOCK_ENTRY':
        return { commands: ['/warehousing/stock-entry'], extras: { queryParams: { stockEntryId: link.resourceId } } };
      case 'WAREHOUSE_STOCK_MOVEMENT':
        return { commands: ['/warehousing/stock'], extras: { queryParams: { stockMovementId: link.resourceId } } };
      case 'WAREHOUSE_DISPATCH':
        return { commands: ['/warehousing/dispatch/view', link.resourceId] };
      case 'BILLING_DRAFT':
        return { commands: ['/warehousing/billing-drafts/view', link.resourceId] };
      default:
        return null;
    }
  }

  receiveAgainstPurchaseOrder(link: ContainerCaseResourceLink): void {
    if (!link.resourceId) return;
    this.router.navigate(['/order/purchase-order/view', link.resourceId, 'receipts', 'new'], {
      queryParams: { containerCaseId: this.caseItem?.id }
    });
  }

  resourceLinkLabel(link: ContainerCaseResourceLink): string {
    switch ((link.resourceType || '').toUpperCase()) {
      case 'SHIPMENT': return 'Shipment';
      case 'SHIPMENT_DELIVERY': return 'Shipment Delivery';
      case 'TRANSPORT_ORDER_RELEASE': return 'Transport Order Release';
      case 'TRANSPORT_EXECUTION_LINK': return 'Transport Execution Link';
      case 'CONTAINER_YARD_VISIT': return 'Container Yard Visit';
      case 'CONTAINER_YARD_MOVE': return 'Container Yard Move';
      case 'CONTAINER_LEG_HANDOFF': return 'Transport-Storage Handoff';
      case 'CLEARANCE_HEADER': return 'Customs Clearance';
      case 'PURCHASE_ORDER': return 'Purchase Order';
      case 'PURCHASE_ORDER_RECEIPT': return 'Goods Receipt';
      case 'WAREHOUSE_STOCK_UNIT': return 'Warehouse Stock Unit';
      case 'WAREHOUSE_STOCK_ENTRY': return 'Warehouse Stock Entry';
      case 'WAREHOUSE_STOCK_MOVEMENT': return 'Warehouse Stock Movement';
      case 'WAREHOUSE_DISPATCH': return 'Warehouse Dispatch';
      case 'BILLING_DRAFT': return 'Billing Draft';
      default: return this.formatCodeLabel(link.resourceType) || 'Linked Record';
    }
  }

  linkedRecordReferenceLabel(link: ContainerCaseResourceLink): string {
    const label = this.safeReferenceLabel(link.label);
    if (label) {
      return label;
    }
    switch ((link.resourceType || '').toUpperCase()) {
      case 'CLEARANCE_HEADER':
      case 'CUSTOMS_CONTAINER':
        return this.caseItem?.customsDeclarationNumber || this.caseItem?.billOfLadingNumber || 'Clearance linked';
      case 'PURCHASE_ORDER':
        return this.caseItem?.bookingNumber || this.caseItem?.billOfLadingNumber || 'Purchase order linked';
      case 'PURCHASE_ORDER_RECEIPT':
        return this.caseItem?.containerNumber || 'Goods receipt linked';
      case 'SHIPMENT':
      case 'SHIPMENT_DELIVERY':
        return this.caseItem?.billOfLadingNumber || this.caseItem?.bookingNumber || 'Shipment linked';
      case 'TRANSPORT_ORDER_RELEASE':
      case 'TRANSPORT_EXECUTION_LINK':
        return this.caseItem?.bookingNumber || this.caseItem?.billOfLadingNumber || 'Transport linked';
      case 'CONTAINER_YARD_VISIT':
      case 'CONTAINER_YARD_MOVE':
        return this.caseItem?.containerNumber || 'Yard activity linked';
      case 'WAREHOUSE_STOCK_UNIT':
      case 'WAREHOUSE_STOCK_ENTRY':
      case 'WAREHOUSE_STOCK_MOVEMENT':
      case 'WAREHOUSE_DISPATCH':
        return this.caseItem?.containerNumber || 'Warehouse activity linked';
      case 'CONTAINER_LEG_HANDOFF':
        return 'Transport-storage handoff';
      case 'BILLING_DRAFT':
        return this.caseItem?.billOfLadingNumber || 'Billing draft linked';
      default:
        return 'Linked record';
    }
  }

  relationTypeLabel(relationType?: string | null): string {
    return this.formatCodeLabel(relationType) || '-';
  }

  handoffYardVisitLabel(yardVisitId?: number | null): string {
    if (!yardVisitId) {
      return '-';
    }
    const visit = this.yardVisits.find(candidate => candidate.id === yardVisitId);
    return visit?.containerNumber || visit?.bookingNumber || visit?.billOfLadingNumber || 'Yard visit linked';
  }

  statusSeverity(status?: string): string {
    switch ((status || '').toUpperCase()) {
      case 'COMPLETED': return 'success';
      case 'ON_HOLD': return 'danger';
      case 'IN_PROGRESS':
      case 'ACTIVE': return 'warn';
      case 'OPEN':
      case 'AWAITING_DISPATCH':
      case 'PLANNED': return 'info';
      case 'CANCELLED': return 'secondary';
      case 'EXCEPTION': return 'danger';
      default: return 'secondary';
    }
  }

  statusTone(status?: string): 'default' | 'success' | 'warn' | 'info' | 'danger' {
    switch ((status || '').toUpperCase()) {
      case 'COMPLETED': return 'success';
      case 'ON_HOLD':
      case 'EXCEPTION': return 'danger';
      case 'IN_PROGRESS':
      case 'ACTIVE': return 'warn';
      case 'OPEN':
      case 'AWAITING_DISPATCH':
      case 'PLANNED': return 'info';
      default: return 'default';
    }
  }

  riskSeverity(status?: string): string {
    switch ((status || '').toUpperCase()) {
      case 'BLOCKED': return 'danger';
      case 'WATCH': return 'warn';
      case 'READY': return 'success';
      case 'NONE': return 'success';
      default: return 'secondary';
    }
  }

  exceptionSeverity(severity?: string): string {
    switch ((severity || '').toLowerCase()) {
      case 'danger': return 'danger';
      case 'warn': return 'warn';
      case 'success': return 'success';
      default: return 'info';
    }
  }

  eventSeverity(sourceModule?: string): string {
    switch ((sourceModule || '').toUpperCase()) {
      case 'CLEARANCE': return 'warn';
      case 'SHIPMENT': return 'info';
      case 'WAREHOUSING': return 'success';
      case 'WAREHOUSING_YARD': return 'warn';
      case 'CONTAINER_HANDOFF_ORCHESTRATION': return 'info';
      case 'HANDOFF': return 'info';
      case 'CONTAINER_TRACKING': return 'secondary';
      default: return 'secondary';
    }
  }

  movementStageLabel(state?: string): string {
    switch ((state || '').toUpperCase()) {
      case 'CASE_OPENED': return 'Case Opened';
      case 'EMPTY_PICKUP': return 'Empty Pickup';
      case 'GATE_OUT_EMPTY': return 'Road Move In Transit';
      case 'GATE_IN_FULL_ORIGIN': return 'Full In-Gate';
      case 'GATE_OUT_FULL': return 'Laden Gate-Out';
      case 'CONTAINER_AVAILABLE': return 'Available for Collection';
      case 'CUSTOMS_RELEASED': return 'Customs Released';
      case 'LINE_RELEASED': return 'Line Released';
      case 'STORAGE_RECEIVED': return 'Yard In';
      case 'STORAGE_SLOT_ASSIGNED': return 'Bay Allocated';
      case 'DESTUFFED_TO_WAREHOUSE': return 'Destuff Completed';
      case 'WAREHOUSE_DISPATCHED': return 'Cargo Dispatched';
      case 'DELIVERED': return 'Laden Delivery Completed';
      case 'EMPTY_RETURNED': return 'Empty Return Completed';
      case 'CASE_CLOSED': return 'Case Closed';
      case 'YARD_GATE_IN':
      case 'YARD_SLOT_ASSIGNED':
      case 'YARD_MOVE':
      case 'YARD_INSPECTION_STARTED':
      case 'YARD_INSPECTION_CLEARED':
      case 'YARD_HOLD_APPLIED':
      case 'YARD_HOLD_RELEASED':
      case 'YARD_DOCK_MOVE':
      case 'YARD_GATE_OUT':
      case 'YARD_EMPTY_RETURNED':
      case 'HANDOFF_TO_STORAGE_PLANNED':
      case 'HANDOFF_TO_STORAGE_COMPLETED':
      case 'HANDOFF_TO_TRANSPORT_PLANNED':
      case 'HANDOFF_TO_TRANSPORT_COMPLETED':
      case 'HANDOFF_BLOCKED':
      case 'HANDOFF_EXCEPTION':
        return this.eventLabel(state);
      default: return this.formatCodeLabel(state) || 'No movement stage';
    }
  }

  movementStageDetail(state?: string): string {
    switch ((state || '').toUpperCase()) {
      case 'EMPTY_PICKUP': return 'Truck planning and pickup release are under way.';
      case 'GATE_OUT_EMPTY': return 'Container has gated out and is moving on the road leg.';
      case 'CONTAINER_AVAILABLE': return 'Container is available for gate-out and onward dispatch.';
      case 'STORAGE_RECEIVED': return 'Container is inside the yard, depot, or storage facility.';
      case 'STORAGE_SLOT_ASSIGNED': return 'A bay or slot has been assigned for dwell control.';
      case 'DESTUFFED_TO_WAREHOUSE': return 'Cargo has been destuffed and handed into warehouse stock.';
      case 'WAREHOUSE_DISPATCHED': return 'Cargo has moved out from warehouse dispatch after destuff.';
      case 'DELIVERED': return 'Laden delivery is completed at consignee or destination yard.';
      case 'EMPTY_RETURNED': return 'Return cycle is completed at the nominated empty depot.';
      case 'CASE_CLOSED': return 'Lifecycle milestones are complete and the case is ready for archive.';
      default: return 'Current lifecycle milestone for this container case.';
    }
  }

  clearanceGateLabel(): string {
    if (this.readiness?.inspectionBlocked) {
      return 'Inspection Hold';
    }
    if (this.readiness?.customsReleased && this.readiness?.lineReleased) {
      return 'Released';
    }
    if (this.readiness?.customsReleased || this.readiness?.lineReleased) {
      return 'Partially Released';
    }
    return 'Pending Release';
  }

  clearanceGateDetail(): string {
    const customs = this.readiness?.customsReleaseStatus || (this.readiness?.customsReleased ? 'RELEASED' : 'PENDING');
    const line = this.readiness?.lineStatus || (this.readiness?.lineReleased ? 'RELEASED' : 'PENDING');
    return `Customs ${customs} | Line ${line}`;
  }

  timelineStageLabel(event: ContainerEvent): string {
    const category = (event.eventCategory || '').toUpperCase();
    const sourceModule = (event.sourceModule || '').toUpperCase();
    const eventCode = (event.eventCode || '').toUpperCase();
    if (category === 'HANDOFF' || sourceModule === 'CONTAINER_HANDOFF_ORCHESTRATION' || eventCode.startsWith('HANDOFF_')) return 'Handoff';
    if (sourceModule === 'WAREHOUSING_YARD' || eventCode.startsWith('YARD_')) return 'Terminal Yard';
    if (category === 'TRANSPORT') return 'Road Movement';
    if (category === 'STORAGE') return 'Yard / Storage';
    if (category === 'CLEARANCE') return 'Clearance';
    if (sourceModule === 'WAREHOUSING') return 'Warehouse Handover';
    return 'Case Control';
  }

  timelineStageSeverity(event: ContainerEvent): string {
    const category = (event.eventCategory || '').toUpperCase();
    const sourceModule = (event.sourceModule || '').toUpperCase();
    const eventCode = (event.eventCode || '').toUpperCase();
    if (eventCode === 'HANDOFF_BLOCKED' || eventCode === 'HANDOFF_EXCEPTION' || eventCode === 'YARD_HOLD_APPLIED') return 'danger';
    if (category === 'HANDOFF' || sourceModule === 'CONTAINER_HANDOFF_ORCHESTRATION' || eventCode.startsWith('HANDOFF_')) return 'info';
    if (sourceModule === 'WAREHOUSING_YARD' || eventCode.startsWith('YARD_')) return 'warn';
    if (category === 'TRANSPORT') return 'info';
    if (category === 'STORAGE') return 'warn';
    if (category === 'CLEARANCE') return 'success';
    if (sourceModule === 'WAREHOUSING') return 'contrast';
    return 'secondary';
  }

  eventLabel(code?: string): string {
    switch ((code || '').toUpperCase()) {
      case 'CASE_OPENED': return 'Case Opened';
      case 'CASE_CLOSED': return 'Case Closed';
      case 'EMPTY_PICKUP': return 'Empty Pickup Started';
      case 'GATE_OUT_EMPTY': return 'Empty Gate-Out';
      case 'GATE_IN_FULL_ORIGIN': return 'Full In-Gate at Origin';
      case 'GATE_OUT_FULL': return 'Laden Gate-Out';
      case 'TRANSPORT_RELEASED': return 'Transport Release Created';
      case 'TRANSPORT_PICKUP_REACHED': return 'Transport Pickup Reached';
      case 'TRANSPORT_DISPATCHED': return 'Transport Dispatched';
      case 'TRANSPORT_ARRIVED': return 'Transport Arrived';
      case 'DELIVERED': return 'Laden Delivery Completed';
      case 'CUSTOMS_RELEASED': return 'Customs Released';
      case 'CUSTOMS_HOLD': return 'Customs Hold';
      case 'LINE_RELEASED': return 'Line Release Completed';
      case 'LINE_HOLD': return 'Line Hold';
      case 'INSPECTION_STARTED': return 'Inspection Started';
      case 'INSPECTION_CLEARED': return 'Inspection Cleared';
      case 'CONTAINER_AVAILABLE': return 'Container Available';
      case 'STORAGE_RECEIVED': return 'Yard In / Storage Received';
      case 'STORAGE_PLANNED': return 'Storage Leg Planned';
      case 'STORAGE_SLOT_ASSIGNED': return 'Bay / Slot Allocated';
      case 'STORAGE_HOLD_APPLIED': return 'Storage Hold Applied';
      case 'STORAGE_HOLD_RELEASED': return 'Storage Hold Released';
      case 'STORAGE_EXITED': return 'Yard Out / Storage Exit';
      case 'DESTUFFED_TO_WAREHOUSE': return 'Destuff Completed';
      case 'WAREHOUSE_DISPATCHED': return 'Warehouse Dispatch Completed';
      case 'EMPTY_RETURNED': return 'Empty Return Completed';
      case 'GATE_IN': return 'Terminal Gate In';
      case 'SLOT_ASSIGN': return 'Terminal Slot Assigned';
      case 'INSPECTION_IN': return 'Inspection Started';
      case 'INSPECTION_OUT': return 'Inspection Cleared';
      case 'HOLD': return 'Yard Hold Applied';
      case 'RELEASE_HOLD': return 'Yard Hold Released';
      case 'DOCK_MOVE': return 'Dock Move';
      case 'GATE_OUT': return 'Terminal Gate Out';
      case 'EMPTY_RETURN': return 'Empty Returned To Yard';
      case 'YARD_GATE_IN': return 'Terminal Gate In';
      case 'YARD_SLOT_ASSIGNED': return 'Terminal Slot Assigned';
      case 'YARD_MOVE': return 'Yard Move';
      case 'YARD_INSPECTION_STARTED': return 'Inspection Started';
      case 'YARD_INSPECTION_CLEARED': return 'Inspection Cleared';
      case 'YARD_HOLD_APPLIED': return 'Yard Hold Applied';
      case 'YARD_HOLD_RELEASED': return 'Yard Hold Released';
      case 'YARD_DOCK_MOVE': return 'Dock Move';
      case 'YARD_GATE_OUT': return 'Terminal Gate Out';
      case 'YARD_EMPTY_RETURNED': return 'Empty Returned To Yard';
      case 'HANDOFF_TO_STORAGE_PLANNED': return 'Storage Handoff Planned';
      case 'HANDOFF_TO_STORAGE_COMPLETED': return 'Storage Handoff Completed';
      case 'HANDOFF_TO_TRANSPORT_PLANNED': return 'Transport Handoff Planned';
      case 'HANDOFF_TO_TRANSPORT_COMPLETED': return 'Transport Handoff Completed';
      case 'HANDOFF_BLOCKED': return 'Handoff Blocked';
      case 'HANDOFF_EXCEPTION': return 'Handoff Exception';
      case 'MANUAL_EXCEPTION': return 'Operational Exception';
      default: return this.formatCodeLabel(code) || 'Lifecycle Event';
    }
  }

  eventCodeSeverity(code?: string): string {
    switch ((code || '').toUpperCase()) {
      case 'HANDOFF_BLOCKED':
      case 'HANDOFF_EXCEPTION':
      case 'YARD_HOLD_APPLIED':
      case 'HOLD':
      case 'STORAGE_HOLD_APPLIED':
      case 'CUSTOMS_HOLD':
      case 'LINE_HOLD':
      case 'MANUAL_EXCEPTION':
        return 'danger';
      case 'YARD_INSPECTION_STARTED':
      case 'INSPECTION_IN':
      case 'INSPECTION_STARTED':
      case 'HANDOFF_TO_STORAGE_PLANNED':
      case 'HANDOFF_TO_TRANSPORT_PLANNED':
      case 'YARD_DOCK_MOVE':
      case 'DOCK_MOVE':
      case 'STORAGE_SLOT_ASSIGNED':
      case 'YARD_SLOT_ASSIGNED':
        return 'info';
      case 'YARD_GATE_IN':
      case 'GATE_IN':
      case 'YARD_MOVE':
      case 'YARD_GATE_OUT':
      case 'GATE_OUT':
      case 'STORAGE_RECEIVED':
      case 'STORAGE_PLANNED':
      case 'STORAGE_EXITED':
        return 'warn';
      case 'YARD_INSPECTION_CLEARED':
      case 'INSPECTION_OUT':
      case 'INSPECTION_CLEARED':
      case 'YARD_HOLD_RELEASED':
      case 'RELEASE_HOLD':
      case 'YARD_EMPTY_RETURNED':
      case 'EMPTY_RETURN':
      case 'HANDOFF_TO_STORAGE_COMPLETED':
      case 'HANDOFF_TO_TRANSPORT_COMPLETED':
      case 'CUSTOMS_RELEASED':
      case 'LINE_RELEASED':
      case 'TRANSPORT_RELEASED':
      case 'TRANSPORT_PICKUP_REACHED':
      case 'TRANSPORT_DISPATCHED':
      case 'TRANSPORT_ARRIVED':
      case 'DELIVERED':
      case 'EMPTY_RETURNED':
      case 'CASE_CLOSED':
        return 'success';
      default:
        return 'secondary';
    }
  }

  eventLocationLine(event: ContainerEvent): string {
    const parts = [event.locationText, event.rawPayloadLocation].filter(Boolean);
    if (parts.length) {
      return parts.join(' | ');
    }
    if (event.sourceReference) {
      return `Reference ${event.sourceReference}`;
    }
    return 'No movement location captured.';
  }

  eventNote(event: ContainerEvent): string {
    return event.remarks || 'No additional operational remarks were recorded for this milestone.';
  }

  eventReferenceLabel(event: ContainerEvent): string {
    if ((event.sourceEntityType || '').toUpperCase() === 'CONTAINER_LEG_HANDOFF' && event.sourceEntityId) {
      return `Handoff #${event.sourceEntityId}`;
    }
    if ((event.sourceEntityType || '').toUpperCase() === 'CONTAINER_YARD_MOVE' && event.sourceEntityId) {
      return `Yard move #${event.sourceEntityId}`;
    }
    if (event.transportLegId) {
      return `Transport leg #${event.transportLegId}`;
    }
    if (event.storageLegId) {
      return `Storage leg #${event.storageLegId}`;
    }
    if (event.sourceReference) {
      return event.sourceReference;
    }
    return '';
  }

  eventSourceLabel(sourceModule?: string): string {
    switch ((sourceModule || '').toUpperCase()) {
      case 'WAREHOUSING_YARD': return 'Terminal Yard';
      case 'CONTAINER_HANDOFF_ORCHESTRATION':
      case 'HANDOFF': return 'Handoff';
      case 'WAREHOUSING': return 'Warehouse';
      case 'CLEARANCE': return 'Clearance';
      case 'SHIPMENT': return 'Shipment';
      case 'CONTAINER_TRACKING': return 'Container Tracking';
      default: break;
    }
    return this.formatCodeLabel(sourceModule) || 'System';
  }

  transportModeLabel(mode?: string | null): string {
    switch ((mode || '').toUpperCase()) {
      case 'TRUCK': return 'Road Move';
      case 'YARD_TRANSFER': return 'Yard Transfer';
      case 'CUSTOMER_DELIVERY': return 'Laden Delivery';
      case 'TERMINAL_TRANSFER': return 'Terminal Handoff';
      case 'EMPTY_RETURN': return 'Empty Return';
      default: return this.formatCodeLabel(mode) || 'Road Leg';
    }
  }

  storageFacilityLabel(type?: string): string {
    switch ((type || '').toUpperCase()) {
      case 'PORT_TERMINAL': return 'Port Terminal';
      case 'BONDED_YARD': return 'Bonded Yard';
      case 'CFS': return 'CFS / Transit Shed';
      case 'WAREHOUSE': return 'CFS / Warehouse Staging';
      case 'CUSTOMER_SITE': return 'Customer Site';
      case 'EMPTY_DEPOT': return 'Empty Depot';
      case 'CARRIER_YARD': return 'Carrier Yard';
      case 'THIRD_PARTY_YARD': return '3PL Yard';
      default: return this.formatCodeLabel(type) || 'Storage Facility';
    }
  }

  transportStatusText(leg: ContainerTransportLeg): string {
    const status = (leg.status || '').toUpperCase();
    if (status === 'EXCEPTION') {
      return leg.exceptionReason || 'Operational exception raised.';
    }
    if (status === 'COMPLETED' || leg.actualDeliveryAt) {
      return 'Delivery milestone completed.';
    }
    if (leg.actualArrivalAt) {
      return 'Arrived at destination checkpoint.';
    }
    if (leg.actualDepartureAt || status === 'ACTIVE') {
      return 'Truck dispatched and moving.';
    }
    if (leg.driverId || leg.vehicleId) {
      return 'Assigned and ready for dispatch.';
    }
    return 'Planning and assignment pending.';
  }

  storageStatusText(leg: ContainerStorageLeg): string {
    if (this.isReferenceOnlyStorageLeg(leg)) {
      return 'Third-party supplier terminal. Operations are handled outside CargoMate.';
    }
    const status = (leg.status || '').toUpperCase();
    if (status === 'EXCEPTION') {
      return leg.notes || 'Storage exception requires action.';
    }
    if (status === 'COMPLETED' || leg.actualOutAt) {
      return 'Storage cycle completed and container exited.';
    }
    if (status === 'ACTIVE' || leg.actualInAt) {
      return 'Container is in active dwell at this facility.';
    }
    return 'Storage planning is pending activation.';
  }

  driverLabel(driverId?: number | null): string {
    if (!driverId) {
      return 'Driver pending';
    }
    const driver = this.drivers.find(item => item.id === driverId);
    return driver?.name || driver?.legalName || `Driver #${driverId}`;
  }

  vehicleLabel(vehicleId?: number | null): string {
    if (!vehicleId) {
      return 'Vehicle pending';
    }
    const vehicle = this.vehicles.find(item => item.vehicleId === vehicleId);
    return vehicle?.plateNumber || vehicle?.registrationNumber || `Vehicle #${vehicleId}`;
  }

  vendorLabel(vendorCompanyId?: number | null): string {
    if (!vendorCompanyId) {
      return 'Transporter pending';
    }
    const vendor = this.vendors.find(item => item.id === vendorCompanyId);
    return vendor?.name || `Vendor #${vendorCompanyId}`;
  }

  milestoneLabel(actual?: Date | string | null, planned?: Date | string | null): string {
    if (actual) {
      return this.dateTimeLabel(actual);
    }
    if (planned) {
      return `Planned ${this.dateTimeLabel(planned)}`;
    }
    return 'Pending';
  }

  dateTimeLabel(value?: Date | string | null): string {
    if (!value) {
      return 'Pending';
    }
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return 'Pending';
    }
    return parsed.toLocaleString([], {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  }

  private formDate(value?: Date | string | null): Date | null {
    if (!value) {
      return null;
    }
    const parsed = value instanceof Date ? value : new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  timeLabel(value?: Date | string | null): string {
    if (!value) {
      return '-';
    }
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return '-';
    }
    return parsed.toLocaleTimeString([], {
      hour: 'numeric',
      minute: '2-digit'
    });
  }

  countTransportByStatus(status: string): number {
    return this.transportLegs.filter(leg => (leg.status || '').toUpperCase() === status.toUpperCase()).length;
  }

  countStorageByStatus(status: string): number {
    return this.storageLegs.filter(leg => (leg.status || '').toUpperCase() === status.toUpperCase()).length;
  }

  blockedHandoffCount(): number {
    return this.handoffs.filter(handoff => ['BLOCKED', 'EXCEPTION'].includes((handoff.status || '').toUpperCase())).length;
  }

  activeYardVisit(): ContainerYardVisit | undefined {
    return this.yardVisits.find(visit => !['GATED_OUT', 'CANCELLED', 'COMPLETED'].includes((visit.status || '').toUpperCase()))
      || this.yardVisits.find(visit => !visit.gateOutAt);
  }

  activeTransportLeg(): ContainerTransportLeg | undefined {
    return this.transportLegs.find(leg => leg.id === this.caseItem?.activeTransportLegId)
      || this.transportLegs.find(leg => (leg.status || '').toUpperCase() === 'ACTIVE');
  }

  activeStorageLeg(): ContainerStorageLeg | undefined {
    return this.storageLegs.find(leg => leg.id === this.caseItem?.activeStorageLegId)
      || this.storageLegs.find(leg => (leg.status || '').toUpperCase() === 'ACTIVE');
  }

  nextPlannedTransportLeg(): ContainerTransportLeg | undefined {
    return this.transportLegs.find(leg => (leg.status || '').toUpperCase() === 'PLANNED');
  }

  nextRequiredHandoff(): ContainerLegHandoff | undefined {
    return this.handoffs.find(handoff => ['BLOCKED', 'EXCEPTION', 'READY', 'PLANNED', 'IN_PROGRESS'].includes((handoff.status || '').toUpperCase()));
  }

  activeTransportDetail(): string {
    const leg = this.activeTransportLeg();
    if (!leg) {
      const planned = this.nextPlannedTransportLeg();
      return planned ? `Next planned: ${this.transportLegLabel(planned.id)} | ${planned.fromLocationText || 'Origin pending'} to ${planned.toLocationText || 'destination pending'}` : 'No active or planned movement leg.';
    }
    const linkedContext = this.linkedOpsLabel(leg);
    const route = `${leg.fromLocationText || 'Origin pending'} to ${leg.toLocationText || 'destination pending'}`;
    return `${linkedContext} | ${route}`;
  }

  activeStorageDetail(): string {
    const leg = this.activeStorageLeg();
    if (!leg) {
      return 'No active terminal, yard, CFS, or WMS storage dwell.';
    }
    const facility = this.facilityLabel(leg.facilityId, leg.facilityName);
    const slot = leg.yardSlotId ? this.yardSlotLabel(leg.yardSlotId, leg.slotCode) : (leg.slotCode || 'Slot pending');
    return `${facility} | ${slot} | ${this.storageBridgeLabel(leg)}`;
  }

  activeYardVisitLabel(): string {
    const visit = this.activeYardVisit();
    if (!visit) {
      return 'No active yard visit';
    }
    return visit.containerNumber || visit.bookingNumber || visit.billOfLadingNumber || 'Active yard visit';
  }

  activeYardVisitDetail(): string {
    const visit = this.activeYardVisit();
    if (!visit) {
      return 'Gate-in has not opened a terminal yard stay.';
    }
    const facility = this.facilityLabel(visit.facilityId);
    const location = this.yardSlotLabel(visit.currentSlotId, visit.currentLocationText);
    const area = this.yardAreaLabel(visit.currentAreaId);
    return `${facility} | ${area} | ${location}`;
  }

  nextHandoffLabel(): string {
    const handoff = this.nextRequiredHandoff();
    return handoff ? this.handoffTypeLabel(handoff.handoffType) : 'No open handoff';
  }

  nextHandoffDetail(): string {
    const handoff = this.nextRequiredHandoff();
    if (!handoff) {
      return 'No planned, blocked, or in-progress transport-storage transition.';
    }
    const fromLabel = handoff.fromTransportLegId ? this.transportLegLabel(handoff.fromTransportLegId) : this.storageLegLabel(handoff.fromStorageLegId);
    const toLabel = handoff.toStorageLegId ? this.storageLegLabel(handoff.toStorageLegId) : this.transportLegLabel(handoff.toTransportLegId);
    return `${fromLabel} -> ${toLabel}${handoff.blockingReason ? ' | ' + handoff.blockingReason : ''}`;
  }

  blockedHandoffDetail(): string {
    const blocked = this.handoffs.filter(handoff => ['BLOCKED', 'EXCEPTION'].includes((handoff.status || '').toUpperCase()));
    if (!blocked.length) {
      return 'No handoff is blocking dispatch or storage exit.';
    }
    const first = blocked[0];
    return `${this.handoffTypeLabel(first.handoffType)}${first.blockingReason ? ' | ' + first.blockingReason : ''}`;
  }

  transportLegLabel(legId?: number | null): string {
    if (!legId) {
      return 'Transport not linked';
    }
    const leg = this.transportLegs.find(item => item.id === legId);
    return leg ? `Transport leg ${leg.sequenceNo || leg.id}` : `Transport leg #${legId}`;
  }

  storageLegLabel(legId?: number | null): string {
    if (!legId) {
      return 'Storage not linked';
    }
    const leg = this.storageLegs.find(item => item.id === legId);
    if (!leg) {
      return `Storage leg #${legId}`;
    }
    const prefix = this.isReferenceOnlyStorageLeg(leg) ? 'Reference terminal' : 'Storage leg';
    return `${prefix} ${leg.sequenceNo || leg.id}${leg.facilityName ? ' - ' + leg.facilityName : ''}`;
  }

  facilityLabel(facilityId?: number | null, fallback?: string | null): string {
    if (!facilityId) {
      return fallback || 'Facility not linked';
    }
    const facility = this.facilities.find(item => item.id === facilityId);
    return facility?.facilityName || fallback || `Facility #${facilityId}`;
  }

  supplierOperatorLabel(operatorCrmClientId?: number | null): string {
    if (!operatorCrmClientId) {
      return 'Supplier not linked';
    }
    const supplier = this.supplierClients.find(item => item.id === operatorCrmClientId);
    return supplier?.companyName || supplier?.clientNumber || `Supplier #${operatorCrmClientId}`;
  }

  isReferenceOnlyFacility(facilityId?: number | null): boolean {
    return !!facilityId && this.facilities.some(facility => facility.id === facilityId && facility.operationMode === 'REFERENCE_ONLY');
  }

  isReferenceOnlyStorageLeg(leg?: ContainerStorageLeg | null): boolean {
    return !!leg && (leg.storageControlMode === 'REFERENCE_ONLY' || this.isReferenceOnlyFacility(leg.facilityId));
  }

  storageDialogReferenceOnly(): boolean {
    const facilityId = this.storageForm?.get('facilityId')?.value;
    return this.isReferenceOnlyFacility(facilityId) || this.storageForm?.get('storageControlMode')?.value === 'REFERENCE_ONLY';
  }

  managedYardFacilities(): ContainerFacility[] {
    return this.facilities.filter(facility => facility.active !== false && facility.operationMode !== 'REFERENCE_ONLY');
  }

  yardAreaLabel(areaId?: number | null): string {
    if (!areaId) {
      return 'Area pending';
    }
    const area = this.yardAreas.find(item => item.id === areaId);
    return area?.areaName || area?.areaCode || `Area #${areaId}`;
  }

  yardSlotLabel(slotId?: number | null, fallback?: string | null): string {
    if (!slotId) {
      return fallback || 'Slot pending';
    }
    const slot = this.yardSlots.find(item => item.id === slotId);
    return slot?.slotCode || fallback || `Slot #${slotId}`;
  }

  storageFacilityOptionsForType(): ContainerFacility[] {
    const selectedType = String(this.storageForm?.get('facilityType')?.value || '').toUpperCase();
    return this.facilities.filter(facility => {
      if (facility.active === false) {
        return false;
      }
      return !selectedType || String(facility.facilityType || '').toUpperCase() === selectedType;
    });
  }

  storageZoneOptions(): WarehousingMasterData[] {
    const warehouseId = this.storageForm?.get('warehouseId')?.value;
    return warehouseId ? this.zoneOptions.filter(zone => zone.parentId === warehouseId) : [];
  }

  storageYardAreaOptions(): ContainerYardArea[] {
    const facilityId = this.storageForm?.get('facilityId')?.value;
    return this.yardAreas.filter(area => {
      if (area.active === false) {
        return false;
      }
      return !facilityId || area.facilityId === facilityId;
    });
  }

  storageYardSlotOptions(): ContainerYardSlot[] {
    const facilityId = this.storageForm?.get('facilityId')?.value;
    const areaId = this.storageForm?.get('yardAreaId')?.value;
    return this.yardSlots.filter(slot => {
      if (slot.active === false) {
        return false;
      }
      if (slot.occupied) {
        return false;
      }
      if (facilityId && slot.facilityId !== facilityId) {
        return false;
      }
      return !areaId || slot.areaId === areaId;
    });
  }

  storageActionYardAreaOptions(): ContainerYardArea[] {
    const facilityId = this.selectedStorageLeg?.facilityId;
    return this.yardAreas.filter(area => area.active !== false && (!facilityId || area.facilityId === facilityId));
  }

  storageActionYardSlotOptions(): ContainerYardSlot[] {
    const facilityId = this.selectedStorageLeg?.facilityId;
    const areaId = this.storageSlotForm?.get('yardAreaId')?.value;
    const currentSlotId = this.selectedStorageLeg?.yardSlotId;
    return this.yardSlots.filter(slot => {
      if (slot.active === false) {
        return false;
      }
      if (slot.occupied && slot.id !== currentSlotId) {
        return false;
      }
      if (facilityId && slot.facilityId !== facilityId) {
        return false;
      }
      return !areaId || slot.areaId === areaId;
    });
  }

  onStorageActionAreaChange(areaId?: number | null): void {
    const slotId = this.storageSlotForm?.get('yardSlotId')?.value;
    const slot = this.yardSlots.find(item => item.id === slotId);
    if (slot && areaId && slot.areaId !== areaId) {
      this.storageSlotForm.patchValue({ yardSlotId: null, slotCode: '' }, { emitEvent: false });
    }
  }

  onStorageActionSlotChange(slotId?: number | null): void {
    const slot = this.yardSlots.find(item => item.id === slotId);
    this.storageSlotForm.patchValue({
      yardAreaId: slot?.areaId || this.storageSlotForm.get('yardAreaId')?.value || null,
      slotCode: slot?.slotCode || this.storageSlotForm.get('slotCode')?.value || ''
    }, { emitEvent: false });
  }

  yardVisitMoves(visitId?: number): ContainerYardMove[] {
    return visitId ? (this.yardMovesByVisit.get(visitId) || []) : [];
  }

  yardVisitCharges(visitId?: number): ContainerYardChargeAccrual[] {
    return visitId ? (this.yardChargesByVisit.get(visitId) || []) : [];
  }

  yardVisitEirs(visitId?: number): ContainerYardEir[] {
    return visitId ? (this.yardEirsByVisit.get(visitId) || []) : [];
  }

  yardVisitChargeTotal(visitId?: number): string {
    const charges = this.yardVisitCharges(visitId);
    if (!charges.length) {
      return 'No estimated charges';
    }
    const currency = charges.find(charge => !!charge.currencyCode)?.currencyCode || 'SAR';
    const total = charges.reduce((sum, charge) => sum + Number(charge.chargeAmount || 0), 0);
    return `${currency} ${total.toFixed(2)}`;
  }

  latestYardEir(visitId?: number): ContainerYardEir | undefined {
    return this.yardVisitEirs(visitId)
      .slice()
      .sort((a, b) => new Date(b.createdAt || b.eventTime || 0).getTime() - new Date(a.createdAt || a.eventTime || 0).getTime())[0];
  }

  openYardEir(eir?: ContainerYardEir): void {
    if (!eir?.id || !this.caseItem?.owningCompanyId) {
      return;
    }
    window.open(this.terminalYardService.eirPdfUrl(this.caseItem.owningCompanyId, eir.id), '_blank');
  }

  recalculateYardCharges(visit: ContainerYardVisit): void {
    if (!visit.id || !this.caseItem?.owningCompanyId) {
      return;
    }
    this.terminalYardService.recalculateCharges(this.caseItem.owningCompanyId, visit.id).subscribe({
      next: charges => {
        this.yardChargesByVisit.set(visit.id!, charges || []);
        this.messageService.add({ severity: 'success', summary: 'Charges recalculated', detail: 'Estimated yard charges were refreshed.' });
        this.loadFacilityAndYardContext();
      },
      error: error => this.messageService.add({
        severity: 'error',
        summary: 'Charge calculation failed',
        detail: error?.error?.message || 'Could not calculate yard charges.'
      })
    });
  }

  handoffTypeLabel(type?: string): string {
    switch ((type || '').toUpperCase()) {
      case 'TRANSPORT_TO_STORAGE': return 'Transport to Storage';
      case 'STORAGE_TO_TRANSPORT': return 'Storage to Transport';
      default: return this.formatCodeLabel(type) || 'Handoff';
    }
  }

  handoffActionTitle(): string {
    switch (this.handoffAction) {
      case 'receiveStorage': return 'Receive Storage Leg';
      case 'exitStorage': return 'Exit Storage / Gate Out';
      default: return 'Storage Handoff Action';
    }
  }

  handoffActionSubtitle(): string {
    switch (this.handoffAction) {
      case 'receiveStorage': return 'Open the storage leg and, when available, complete the inbound transport-to-storage handoff.';
      case 'exitStorage': return 'Close the storage dwell and link the outbound transport leg for storage-to-transport handoff.';
      default: return 'Complete the storage-owned lifecycle transition context.';
    }
  }

  handoffGuidance(): string {
    switch (this.handoffAction) {
      case 'receiveStorage':
        return 'Select the inbound source transport leg when this storage receipt closes a transport-to-storage handoff.';
      case 'exitStorage':
        return 'Select the outbound target transport leg when this storage exit starts the next movement.';
      default:
        return 'Review leg, facility, and override context before submitting.';
    }
  }

  handoffSubmitLabel(): string {
    switch (this.handoffAction) {
      case 'receiveStorage': return 'Receive Storage';
      case 'exitStorage': return 'Exit Storage';
      default: return 'Complete Action';
    }
  }

  showAutoCreateStorage(): boolean {
    return false;
  }

  requiresOverrideReason(): boolean {
    return false;
  }

  dispatchReadinessLabel(leg: ContainerTransportLeg): string {
    const activeStorage = this.activeStorageLeg();
    if (!activeStorage || activeStorage.id === leg.sourceStorageLegId) {
      return 'Dispatch ready';
    }
    const completedHandoff = this.handoffs.some(handoff =>
      handoff.handoffType === 'STORAGE_TO_TRANSPORT'
      && handoff.fromStorageLegId === activeStorage.id
      && handoff.toTransportLegId === leg.id
      && (handoff.status || '').toUpperCase() === 'COMPLETED');
    return completedHandoff ? 'Storage handoff complete' : 'Blocked by active storage';
  }

  transportOwnershipLabel(leg: ContainerTransportLeg): string {
    if (leg.shipmentDeliveryId || leg.executionLinkId) {
      return 'Managed by Shipment Execution';
    }
    if (leg.transportReleaseId) {
      return 'Managed by Transport Planning';
    }
    return 'Legacy transport record';
  }

  transportOwnershipDetail(leg: ContainerTransportLeg): string {
    if (leg.shipmentDeliveryId || leg.shipmentNumber || leg.deliveryNumber) {
      return this.shipmentDeliveryReference(leg);
    }
    if (leg.executionLinkId) {
      return 'Shipment execution context linked';
    }
    if (leg.transportReleaseId) {
      return leg.transportReleaseNumber || 'Transport release linked';
    }
    return 'Historical record created before journey-plan enforcement.';
  }

  shipmentContextLabel(leg: ContainerTransportLeg): string {
    if (leg.shipmentDeliveryId) {
      return 'Shipment / Delivery Linked';
    }
    if (leg.executionLinkId) {
      return 'Execution Link Linked';
    }
    if (leg.transportReleaseId) {
      return 'Awaiting Planning Execution';
    }
    return 'No Shipment Execution';
  }

  shipmentContextDetail(leg: ContainerTransportLeg): string {
    const reference = this.shipmentDeliveryReference(leg);
    if (reference) {
      return reference;
    }
    return leg.executionLinkId
      ? 'Shipment execution is linked; shipment and delivery references are loading.'
      : 'Shipment and delivery references will appear after planning reconciliation.';
  }

  canOpenShipmentDelivery(leg: ContainerTransportLeg): boolean {
    return !!(leg.shipmentDeliveryId || leg.executionLinkId);
  }

  shouldShowTransportReconcile(leg: ContainerTransportLeg): boolean {
    return !!leg.transportReleaseId && !leg.executionLinkId && !leg.shipmentDeliveryId;
  }

  isStorageLegActive(leg: ContainerStorageLeg): boolean {
    return !['COMPLETED', 'CANCELLED'].includes((leg.status || '').toUpperCase());
  }

  isStorageLegPlanned(leg: ContainerStorageLeg): boolean {
    return (leg.status || '').toUpperCase() === 'PLANNED';
  }

  isStorageLegOnHold(leg: ContainerStorageLeg): boolean {
    return (leg.status || '').toUpperCase() === 'EXCEPTION';
  }

  linkedOpsLabel(leg: ContainerTransportLeg): string {
    const shipmentReference = this.shipmentDeliveryReference(leg);
    if (shipmentReference) {
      return shipmentReference;
    }
    if (leg.executionLinkId) {
      return 'Shipment execution linked';
    }
    if (leg.transportReleaseId) {
      return leg.transportReleaseNumber || 'Transport release linked';
    }
    return 'Legacy transport record';
  }

  linkedOpsDetail(leg: ContainerTransportLeg): string {
    const parts = [
      leg.executionLinkId ? 'Shipment execution context reconciled' : '',
      leg.sourceStorageLegId ? `From ${this.storageLegLabel(leg.sourceStorageLegId)}` : '',
      leg.destinationStorageLegId ? `To ${this.storageLegLabel(leg.destinationStorageLegId)}` : ''
    ].filter(Boolean);
    return parts.length ? parts.join(' | ') : 'No shipment stop or storage linkage';
  }

  private shipmentDeliveryReference(leg: ContainerTransportLeg): string {
    const parts = [
      leg.shipmentNumber ? `Shipment ${leg.shipmentNumber}` : '',
      leg.deliveryNumber ? `Delivery ${leg.deliveryNumber}` : ''
    ].filter(Boolean);
    if (parts.length) {
      return parts.join(' | ');
    }
    return leg.shipmentDeliveryId ? 'Shipment delivery linked; reference number pending' : '';
  }

  storageBridgeLabel(leg: ContainerStorageLeg): string {
    if (leg.yardVisitId) {
      return `Yard visit #${leg.yardVisitId}`;
    }
    if (leg.stockEntryId) {
      return `Stock entry #${leg.stockEntryId}`;
    }
    if (leg.stockMovementId) {
      return `Stock movement #${leg.stockMovementId}`;
    }
    return leg.sourceModule || 'Manual storage';
  }

  storageBridgeDetail(leg: ContainerStorageLeg): string {
    const parts = [
      leg.inboundTransportLegId ? `Inbound ${this.transportLegLabel(leg.inboundTransportLegId)}` : '',
      leg.outboundTransportLegId ? `Outbound ${this.transportLegLabel(leg.outboundTransportLegId)}` : '',
      leg.yardAreaId ? this.yardAreaLabel(leg.yardAreaId) : '',
      leg.yardSlotId ? this.yardSlotLabel(leg.yardSlotId, leg.slotCode) : ''
    ].filter(Boolean);
    return parts.length ? parts.join(' | ') : (leg.sourceReference || 'No bridge reference recorded');
  }

  private initForms(): void {
    this.storageForm = this.fb.group({
      facilityType: ['BONDED_YARD', Validators.required],
      facilityId: [null, Validators.required],
      facilityName: [''],
      warehouseId: [null],
      zoneId: [null],
      yardAreaId: [null],
      yardSlotId: [null],
      slotCode: [''],
      storageControlMode: ['MANAGED'],
      operatorCrmClientId: [null],
      inboundTransportLegId: [null],
      outboundTransportLegId: [null],
      storageReason: ['TERMINAL_YARD'],
      plannedInAt: [null],
      plannedOutAt: [null],
      freeTimeStartAt: [null],
      freeTimeEndAt: [null],
      chargeableFromAt: [null],
      chargeableToAt: [null],
      sourceReference: [''],
      notes: ['']
    });
    this.handoffForm = this.fb.group({
      eventTime: [new Date()],
      sourceStorageLegId: [null],
      targetStorageLegId: [null],
      sourceTransportLegId: [null],
      targetTransportLegId: [null],
      facilityType: [null],
      facilityId: [null],
      warehouseId: [null],
      yardVisitId: [null],
      locationId: [null],
      locationText: [''],
      autoCreateStorageLeg: [false],
      overrideReason: [''],
      remarks: ['']
    });
    this.storageSlotForm = this.fb.group({
      yardAreaId: [null],
      yardSlotId: [null],
      slotCode: [''],
      notes: ['']
    });
    this.storageHoldForm = this.fb.group({
      eventTime: [new Date()],
      notes: ['']
    });
    this.storageReleaseForm = this.fb.group({
      eventTime: [new Date()],
      notes: ['']
    });
    this.yardGateInForm = this.fb.group({
      facilityId: [null, Validators.required],
      appointmentId: [null],
      initialAreaId: [null],
      initialSlotId: [null],
      inboundTransportLegId: [null],
      gateInAt: [new Date(), Validators.required],
      freeTimeStartAt: [null],
      freeTimeEndAt: [null],
      emptyFullStatus: ['UNKNOWN'],
      sealVerified: [true],
      sealMismatchReason: [''],
      conditionStatus: ['UNKNOWN'],
      damageObserved: [false],
      damageNotes: [''],
      carrierName: [''],
      truckPlateNumber: [''],
      chassisNumber: [''],
      remarks: ['']
    });
    this.yardGateOutForm = this.fb.group({
      gateOutAt: [new Date(), Validators.required],
      emptyReturn: [false],
      outboundTransportLegId: [null],
      referenceNumber: [''],
      sealVerified: [true],
      sealMismatchReason: [''],
      conditionStatus: ['UNKNOWN'],
      damageObserved: [false],
      damageNotes: [''],
      remarks: ['']
    });
    this.yardMoveForm = this.fb.group({
      moveType: ['YARD_MOVE', Validators.required],
      toAreaId: [null],
      toSlotId: [null],
      eventTime: [new Date(), Validators.required],
      referenceNumber: [''],
      remarks: ['']
    });
    this.journeyItemForm = this.fb.group({
      itemType: ['TRANSPORT', Validators.required],
      stepName: [''],
      transportMode: ['TRUCK'],
      originLocationId: [null],
      destinationLocationId: [null],
      predecessorPlanItemId: [null],
      locationId: [null],
      locationText: [''],
      facilityType: ['PORT_TERMINAL'],
      facilityId: [null],
      storageControlMode: ['MANAGED'],
      storageReason: ['TERMINAL_YARD'],
      plannedStartAt: [null, Validators.required],
      plannedEndAt: [null, Validators.required],
      priority: ['NORMAL'],
      vehicleTypeId: [null],
      routeTemplateId: [null],
      assignmentRuleId: [null],
      manpowerCount: [null],
      helperCount: [null],
      thirdPartyEligible: [null],
      requiredTools: [''],
      sourceStoragePlanItemId: [null],
      destinationStoragePlanItemId: [null],
      remarks: ['']
    }, { validators: [this.journeyPlannedWindowValidator] });
    this.journeyTemplateForm = this.fb.group({
      templateCode: ['', Validators.required],
      templateName: ['', Validators.required],
      description: [''],
      journeyType: [''],
      applicableEquipmentTypes: ['']
    });
  }

  private openHandoffDialog(action: 'receiveStorage' | 'exitStorage', transportLeg?: ContainerTransportLeg, storageLeg?: ContainerStorageLeg): void {
    this.handoffAction = action;
    this.selectedTransportLeg = transportLeg;
    this.selectedStorageLeg = storageLeg;
    const activeStorage = this.activeStorageLeg();
    const activeTransport = this.activeTransportLeg();
    const facilityId = storageLeg?.facilityId || activeStorage?.facilityId || undefined;
    const facility = facilityId ? this.facilities.find(item => item.id === facilityId) : undefined;
    this.handoffForm.reset({
      eventTime: new Date(),
      sourceStorageLegId: storageLeg?.id || null,
      targetStorageLegId: storageLeg?.id || null,
      sourceTransportLegId: action === 'receiveStorage' ? storageLeg?.inboundTransportLegId || activeTransport?.id || null : null,
      targetTransportLegId: action === 'exitStorage' ? storageLeg?.outboundTransportLegId || this.nextPlannedTransportLeg()?.id || null : null,
      facilityType: storageLeg?.facilityType || activeStorage?.facilityType || facility?.facilityType || null,
      facilityId: facilityId || null,
      warehouseId: storageLeg?.warehouseId || activeStorage?.warehouseId || null,
      yardVisitId: storageLeg?.yardVisitId || activeStorage?.yardVisitId || this.activeYardVisit()?.id || null,
      locationId: null,
      locationText: storageLeg?.facilityName || activeStorage?.facilityName || '',
      autoCreateStorageLeg: false,
      overrideReason: '',
      remarks: ''
    });
    this.handoffDialogVisible = true;
  }

  saveHandoffAction(): void {
    if (!this.caseItem?.id || !this.handoffAction) {
      return;
    }
    const value = this.handoffForm.getRawValue();
    if (!this.validateHandoffAction(value)) {
      return;
    }
    const handoff = this.buildHandoffContext(value);
    this.isSubmitting = true;

    const request: Observable<unknown> | null = this.handoffAction === 'receiveStorage' && this.selectedStorageLeg?.id
      ? this.containerTrackingService.receiveStorageLeg(this.caseItem.id, this.selectedStorageLeg.id, {
        eventTime: value.eventTime || new Date(),
        facilityName: this.selectedStorageLeg.facilityName || value.locationText || undefined,
        notes: value.remarks || undefined,
        updatedBy: this.currentUserId(),
        handoff
      })
      : this.handoffAction === 'exitStorage' && this.selectedStorageLeg?.id
        ? this.containerTrackingService.exitStorageLeg(this.caseItem.id, this.selectedStorageLeg.id, {
          eventTime: value.eventTime || new Date(),
          notes: value.remarks || this.selectedStorageLeg.notes || undefined,
          updatedBy: this.currentUserId(),
          handoff
        })
        : null;

    if (!request) {
      this.isSubmitting = false;
      return;
    }

    request.subscribe({
      next: () => {
        this.isSubmitting = false;
        this.handoffDialogVisible = false;
        this.handoffAction = null;
        this.selectedTransportLeg = undefined;
        this.selectedStorageLeg = undefined;
        this.messageService.add({ severity: 'success', summary: 'Updated', detail: 'Lifecycle handoff action completed.' });
        this.loadCase();
      },
      error: (error: any) => {
        this.isSubmitting = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Action blocked',
          detail: error?.error?.message || 'Could not complete the handoff-aware action.'
        });
      }
    });
  }

  private buildHandoffContext(value: any): ContainerLegHandoffContext {
    return {
      sourceStorageLegId: value.sourceStorageLegId || undefined,
      targetStorageLegId: value.targetStorageLegId || undefined,
      sourceTransportLegId: value.sourceTransportLegId || undefined,
      targetTransportLegId: value.targetTransportLegId || undefined,
      facilityType: value.facilityType || undefined,
      facilityId: value.facilityId || undefined,
      warehouseId: value.warehouseId || undefined,
      yardVisitId: value.yardVisitId || undefined,
      locationId: value.locationId || undefined,
      locationText: value.locationText || undefined,
      autoCreateStorageLeg: !!value.autoCreateStorageLeg,
      overrideReason: value.overrideReason || undefined
    };
  }

  private validateHandoffAction(value: any): boolean {
    if (this.handoffForm.invalid) {
      this.handoffForm.markAllAsTouched();
      return false;
    }
    const action = this.handoffAction;
    const overrideReason = (value.overrideReason || '').trim();

    if (action === 'receiveStorage' && !value.sourceTransportLegId && !overrideReason) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Inbound transport required',
        detail: 'Select the source transport leg for this storage receipt, or provide an override reason.'
      });
      return false;
    }

    if (action === 'exitStorage' && !value.targetTransportLegId && !overrideReason) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Outbound transport required',
        detail: 'Select the target transport leg for this storage exit, or provide an override reason.'
      });
      return false;
    }

    return true;
  }

  private loadFacilityAndYardContext(): void {
    if (!this.caseItem?.owningCompanyId) {
      return;
    }
    const companyId = this.caseItem.owningCompanyId;
    this.facilityService.findAll(new HttpParams()
      .set('owningCompanyId', String(companyId))
      .set('size', '200')
      .set('sort', 'facilityName,asc'))
      .subscribe({
        next: page => this.facilities = page.content || [],
        error: () => this.facilities = []
      });
    this.crmService.getClients(companyId, ['SUPPLIER', 'WAREHOUSE_SUPPLIER', 'TRANSPORTER']).subscribe({
      next: rows => this.supplierClients = rows || [],
      error: () => this.supplierClients = []
    });
    this.terminalYardService.findAreas(new HttpParams().set('companyId', String(companyId))).subscribe({
      next: areas => this.yardAreas = areas || [],
      error: () => this.yardAreas = []
    });
    this.terminalYardService.findSlots(new HttpParams().set('companyId', String(companyId))).subscribe({
      next: slots => this.yardSlots = slots || [],
      error: () => this.yardSlots = []
    });
    this.terminalYardService.findAppointments(companyId, new HttpParams()
      .set('status', 'SCHEDULED')
      .set('containerNumber', this.caseItem.containerNumber || ''))
      .subscribe({
        next: appointments => this.yardAppointments = appointments || [],
        error: () => this.yardAppointments = []
      });
    this.warehousingService.getMasterData('WAREHOUSE').subscribe({
      next: response => this.warehouseOptions = response.data || [],
      error: () => this.warehouseOptions = []
    });
    this.warehousingService.getMasterData('ZONE').subscribe({
      next: response => this.zoneOptions = response.data || [],
      error: () => this.zoneOptions = []
    });
    this.terminalYardService.findVisits(new HttpParams()
      .set('companyId', String(companyId))
      .set('containerCaseId', String(this.caseItem.id || 0)))
      .subscribe({
        next: visits => {
          this.yardVisits = visits || [];
          this.loadYardMoves();
          this.loadYardCommercials();
        },
        error: () => {
          this.yardVisits = [];
          this.yardMovesByVisit.clear();
          this.yardChargesByVisit.clear();
          this.yardEirsByVisit.clear();
        }
      });
  }

  private loadYardMoves(): void {
    this.yardMovesByVisit.clear();
    this.yardVisits.filter(visit => !!visit.id).forEach(visit => {
      this.terminalYardService.findMoves(visit.id!).subscribe({
        next: moves => this.yardMovesByVisit.set(visit.id!, moves || []),
        error: () => this.yardMovesByVisit.set(visit.id!, [])
      });
    });
  }

  private loadYardCommercials(): void {
    this.yardChargesByVisit.clear();
    this.yardEirsByVisit.clear();
    this.yardVisits.filter(visit => !!visit.id).forEach(visit => {
      this.terminalYardService.findCharges(visit.id!).subscribe({
        next: charges => this.yardChargesByVisit.set(visit.id!, charges || []),
        error: () => this.yardChargesByVisit.set(visit.id!, [])
      });
      this.terminalYardService.findEirs(visit.id!).subscribe({
        next: eirs => this.yardEirsByVisit.set(visit.id!, eirs || []),
        error: () => this.yardEirsByVisit.set(visit.id!, [])
      });
    });
  }

  private resolveCaseTabValue(tabKey: string | null): number {
    if (!tabKey) {
      return 0;
    }
    return this.caseTabKeys.find(tab => tab.key === tabKey.toLowerCase())?.value ?? 0;
  }

  private loadExecutionResources(): void {
    const company = this.sessionStorageService.getCompany();
    const companyId = company?.id || 0;
    if (!companyId || (!this.canManageLegs && !this.canManageStorage)) {
      return;
    }
    this.vehicleTypes = this.sessionStorageService.getVehicleTypes() || [];
    this.driverService.getFreelanceDriversByCompanyId(0, 200, companyId).subscribe({
      next: response => this.drivers = response.items || [],
      error: () => this.drivers = []
    });
    this.vehicleService.getFreelanceVehicleMinsByCompanyId(companyId).subscribe({
      next: response => this.vehicles = response.items || [],
      error: () => this.vehicles = []
    });
    this.locationService.getLocationsByCompanyId(0, 25, companyId).subscribe({
      next: response => this.locations = response.items || [],
      error: () => this.locations = []
    });
    this.transportPlanningService.getPosmRouteTemplates(companyId).subscribe({
      next: response => this.routeTemplates = response || [],
      error: () => this.routeTemplates = []
    });
    this.transportPlanningService.getPosmAssignmentRules(companyId).subscribe({
      next: response => this.assignmentRules = response || [],
      error: () => this.assignmentRules = []
    });
    this.containerTrackingService.findJourneyTemplates(companyId).subscribe({
      next: response => this.journeyTemplates = response || [],
      error: () => this.journeyTemplates = []
    });
    if (company?.countryId) {
      this.companyService.getCompaniesByCountry(company.countryId).subscribe({
        next: response => {
          this.vendors = (response || []).filter(candidate => candidate.id !== companyId);
        },
        error: () => this.vendors = []
      });
    }
  }

  private executeTransportAction(action: string, leg: ContainerTransportLeg, requestFactory: () => any): void {
    if (!this.caseItem?.id || !leg.id) {
      return;
    }
    if (!window.confirm(`Confirm ${action} for transport leg #${leg.sequenceNo}?`)) {
      return;
    }
    this.isSubmitting = true;
    requestFactory().subscribe({
      next: () => {
        this.isSubmitting = false;
        this.messageService.add({ severity: 'success', summary: 'Updated', detail: `Transport leg ${action} completed.` });
        this.loadCase();
      },
      error: (error: any) => {
        this.isSubmitting = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Update failed',
          detail: error?.error?.message || `Could not ${action} this transport leg.`
        });
      }
    });
  }

  private executeStorageAction(action: string, leg: ContainerStorageLeg, requestFactory: () => any): void {
    if (!this.caseItem?.id || !leg.id) {
      return;
    }
    if (!window.confirm(`Confirm ${action} for storage leg #${leg.sequenceNo}?`)) {
      return;
    }
    this.isSubmitting = true;
    requestFactory().subscribe({
      next: () => {
        this.isSubmitting = false;
        this.messageService.add({ severity: 'success', summary: 'Updated', detail: `Storage leg ${action} completed.` });
        this.loadCase();
      },
      error: (error: any) => {
        this.isSubmitting = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Update failed',
          detail: error?.error?.message || `Could not ${action} this storage leg.`
        });
      }
    });
  }

  private currentUserId(): number | undefined {
    return this.sessionStorageService.getCustomer()?.id;
  }

  private resolveOwningCompanyId(): number {
    return this.caseItem?.owningCompanyId
      || this.sessionStorageService.getCompany()?.id
      || this.sessionStorageService.getCustomer()?.company?.id
      || 0;
  }

  private logisticsProjectLabel(project?: LogisticsProject | null): string {
    if (!project) {
      return '';
    }
    return [project.projectNumber, project.projectName].filter(Boolean).join(' - ')
      || project.projectName
      || project.projectNumber
      || '';
  }

  private companyReferenceLabel(company?: Company | null): string {
    if (!company) {
      return '';
    }
    const reference = company.crNumber ? `CR ${company.crNumber}` : '';
    return [reference, company.name].filter(Boolean).join(' - ') || company.name || reference;
  }

  private crmClientReferenceLabel(client?: CrmClient | null): string {
    if (!client) {
      return '';
    }
    return [client.clientNumber, client.companyName].filter(Boolean).join(' - ')
      || client.companyName
      || client.clientNumber
      || '';
  }

  private crmAccountReferenceLabel(account?: CrmAccount | null): string {
    if (!account) {
      return '';
    }
    return [account.category, account.companyName].filter(Boolean).join(' - ')
      || account.companyName
      || account.category
      || '';
  }

  private firstSafeLinkLabel(...resourceTypes: string[]): string {
    const allowedTypes = resourceTypes.map(type => type.toUpperCase());
    const link = this.links.find(candidate => allowedTypes.includes((candidate.resourceType || '').toUpperCase()));
    return this.safeReferenceLabel(link?.label);
  }

  private safeReferenceLabel(label?: string | null): string {
    const trimmed = (label || '').trim();
    if (!trimmed) {
      return '';
    }
    if (/#\d+\b/.test(trimmed)) {
      return '';
    }
    if (/^\d+$/.test(trimmed)) {
      return '';
    }
    if (/^(project|company|customer|crm client|crm account|visit|record|resource)\s+\d+$/i.test(trimmed)) {
      return '';
    }
    return trimmed;
  }

  private nullableNumber(value: unknown): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  private groupTimeline(events: ContainerEvent[]): TimelineGroup[] {
    const grouped = new Map<string, TimelineGroup>();
    const sortedEvents = [...events].sort((left, right) => this.eventTimestamp(left) - this.eventTimestamp(right));

    sortedEvents.forEach(event => {
      const eventDate = this.eventDate(event);
      const key = `${eventDate.getFullYear()}-${eventDate.getMonth()}-${eventDate.getDate()}`;
      if (!grouped.has(key)) {
        grouped.set(key, {
          date: eventDate,
          label: eventDate.toLocaleDateString([], {
            weekday: 'short',
            day: '2-digit',
            month: 'short',
            year: 'numeric'
          }),
          eventCountLabel: '',
          events: []
        });
      }
      grouped.get(key)!.events.push(event);
    });

    return Array.from(grouped.values())
      .sort((left, right) => right.date.getTime() - left.date.getTime())
      .map(group => ({
        ...group,
        eventCountLabel: `${group.events.length} event${group.events.length === 1 ? '' : 's'}`
      }));
  }

  private applyEta(eta?: ContainerCaseEta): void {
    if (!eta) {
      this.etaLabel = '-';
      this.etaDetail = '';
      return;
    }

    const targetLabel = eta.targetAt ? new Date(eta.targetAt).toLocaleString() : null;
    this.etaLabel = eta.destinationLabel || eta.status || '-';
    this.etaDetail = eta.detail || (targetLabel ? `Next land milestone: ${targetLabel}` : '');
  }

  private readinessLabel(): string {
    if (!this.readiness) {
      return '-';
    }
    if (this.readiness.containerAvailable) {
      return 'Available';
    }
    if (this.readiness.inspectionBlocked) {
      return 'Inspection';
    }
    if (this.readiness.customsReleased || this.readiness.lineReleased) {
      return 'Partial';
    }
    return 'Pending';
  }

  private readinessTone(): 'default' | 'success' | 'warn' | 'info' | 'danger' {
    if (!this.readiness) {
      return 'default';
    }
    if (this.readiness.containerAvailable) {
      return 'success';
    }
    if (this.readiness.inspectionBlocked) {
      return 'danger';
    }
    if (this.readiness.customsReleased || this.readiness.lineReleased) {
      return 'warn';
    }
    return 'default';
  }

  private hasExceptionSeverity(severity: string): boolean {
    return this.exceptions.some(exception => (exception.severity || '').toLowerCase() === severity.toLowerCase());
  }

  private formatCodeLabel(value?: string | null): string {
    if (!value) {
      return '';
    }
    return value
      .toLowerCase()
      .split('_')
      .filter(Boolean)
      .map(part => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  }

  private eventDate(event: ContainerEvent): Date {
    const value = new Date(event.eventTime || event.createdAt || new Date());
    return Number.isNaN(value.getTime()) ? new Date() : value;
  }

  private eventTimestamp(event: ContainerEvent): number {
    return this.eventDate(event).getTime();
  }
}
